// W37 — Yoga Poses
// =================================
// Innovative event-detection window. Each "Pose" tracks a specific structural
// crossover pattern between consecutive draws.
//
// Pose 1 — LS1 → Ball 1:
//   For every pair of consecutive draws (N-1, N), detect when LS1 of draw N-1
//   becomes Ball 1 in draw N. Reports lifetime occurrences (pre-2026 + 2026),
//   average bounce-back interval, current gap, and the chronological event log.
import React, { useState, useEffect, useRef } from 'react';
import { Toaster, toast } from 'sonner';
import {
  Sparkle, ArrowLeft, Sun, CircleNotch, ArrowRight, DownloadSimple,
  Calendar, Clock, ChartLine, Stairs,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { W37NeverMonthPanel } from '../components/W37NeverMonthPanel';
import { useMobileTableCards } from '../hooks/useMobileTableCards';
import { useW37Data } from '../hooks/useW37Data';
import { W37LoadNotice } from '../components/W37LoadNotice';
import { W37VisibleExcelButton } from '../components/w37/W37VisibleExcelButton';
import { neverMonthName, readNeverCandidates } from '../components/w37NeverCandidates';
import '../components/w37-mobile-tables.css';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';

// Reusable stat card
const StatCard = ({ label, value, tone = 'amber', icon: Icon, suffix, testid }) => {
  const tones = {
    amber: 'border-amber-300 bg-amber-50 text-amber-900',
    rose: 'border-rose-300 bg-rose-50 text-rose-900',
    emerald: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    blue: 'border-blue-300 bg-blue-50 text-blue-900',
    purple: 'border-purple-300 bg-purple-50 text-purple-900',
    slate: 'border-slate-300 bg-slate-50 text-slate-900',
  };
  return (
    <div className={`rounded-xl border-2 px-4 py-3 ${tones[tone]}`} data-testid={testid} data-w37-export-stat={label}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} weight="bold" />}
        <p className="text-[10px] uppercase tracking-wider font-extrabold">{label}</p>
      </div>
      <p className="text-2xl font-black" style={{ fontFamily: 'var(--font-heading)' }} data-w37-export-value="true">
        {value ?? '—'}{suffix && <span className="text-sm font-bold ml-1">{suffix}</span>}
      </p>
    </div>
  );
};

const PoseBadge = ({ children, tone = 'amber' }) => {
  const tones = {
    amber: 'bg-amber-200 text-amber-900',
    rose: 'bg-rose-200 text-rose-900',
    emerald: 'bg-emerald-200 text-emerald-900',
    slate: 'bg-slate-200 text-slate-900',
  };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-extrabold ${tones[tone]}`}>{children}</span>;
};

const Pose1Card = ({ pose, totalDraws, accent = 'amber', icon: Icon = Sparkle, flowPairs = [['LS₁', 'B₁']] }) => {
  if (!pose) return null;
  const gapTone = pose.current_gap >= (pose.avg_bounce_back || 0) ? 'rose' : 'emerald';
  const accentRing = {
    amber: 'border-amber-300',
    rose: 'border-rose-300',
    indigo: 'border-indigo-300',
    emerald: 'border-emerald-300',
    teal: 'border-teal-300',
    cyan: 'border-cyan-300',
    sky: 'border-sky-300',
    orange: 'border-orange-300',
    lime: 'border-lime-300',
    yellow: 'border-yellow-400',
  }[accent] || 'border-amber-300';
  const accentBg = {
    amber: 'from-orange-100 via-amber-100 to-rose-100 border-amber-200',
    rose: 'from-rose-100 via-pink-100 to-fuchsia-100 border-rose-200',
    indigo: 'from-indigo-100 via-blue-100 to-cyan-100 border-indigo-200',
    emerald: 'from-emerald-100 via-teal-100 to-cyan-100 border-emerald-200',
    teal: 'from-teal-100 via-cyan-100 to-sky-100 border-teal-200',
    cyan: 'from-cyan-100 via-sky-100 to-blue-100 border-cyan-200',
    sky: 'from-sky-100 via-blue-100 to-indigo-100 border-sky-200',
    orange: 'from-orange-100 via-amber-100 to-yellow-100 border-orange-200',
    lime: 'from-lime-100 via-green-100 to-emerald-100 border-lime-200',
    yellow: 'from-yellow-100 via-amber-100 to-orange-100 border-yellow-300',
  }[accent] || 'from-orange-100 via-amber-100 to-rose-100 border-amber-200';
  const accentBadge = {
    amber: 'amber',
    rose: 'rose',
    indigo: 'slate',
    emerald: 'emerald',
    teal: 'emerald',
    cyan: 'slate',
    sky: 'slate',
    orange: 'amber',
    lime: 'emerald',
    yellow: 'amber',
  }[accent] || 'amber';
  const accentIconBg = {
    amber: 'from-amber-400 to-rose-500',
    rose: 'from-rose-400 to-fuchsia-500',
    indigo: 'from-indigo-500 to-blue-600',
    emerald: 'from-emerald-500 to-teal-600',
    teal: 'from-teal-500 to-cyan-600',
    cyan: 'from-cyan-500 to-sky-600',
    sky: 'from-sky-500 to-blue-600',
    orange: 'from-orange-500 to-amber-600',
    lime: 'from-lime-500 to-green-600',
    yellow: 'from-yellow-500 to-amber-600',
  }[accent] || 'from-amber-400 to-rose-500';
  const accentChip = {
    amber: 'border-amber-400 text-amber-700',
    rose: 'border-rose-400 text-rose-700',
    indigo: 'border-indigo-400 text-indigo-700',
    emerald: 'border-emerald-400 text-emerald-700',
    teal: 'border-teal-400 text-teal-700',
    cyan: 'border-cyan-400 text-cyan-700',
    sky: 'border-sky-400 text-sky-700',
    orange: 'border-orange-400 text-orange-700',
    lime: 'border-lime-400 text-lime-700',
    yellow: 'border-yellow-500 text-yellow-700',
  }[accent] || 'border-amber-400 text-amber-700';
  const accentChipSolid = {
    amber: 'bg-amber-500 border-amber-600',
    rose: 'bg-rose-500 border-rose-600',
    indigo: 'bg-indigo-500 border-indigo-600',
    emerald: 'bg-emerald-500 border-emerald-600',
    teal: 'bg-teal-500 border-teal-600',
    cyan: 'bg-cyan-500 border-cyan-600',
    sky: 'bg-sky-500 border-sky-600',
    orange: 'bg-orange-500 border-orange-600',
    lime: 'bg-lime-500 border-lime-600',
    yellow: 'bg-yellow-500 border-yellow-600',
  }[accent] || 'bg-amber-500 border-amber-600';
  const accentNumPill = {
    amber: 'bg-amber-200 text-amber-900',
    rose: 'bg-rose-200 text-rose-900',
    indigo: 'bg-indigo-200 text-indigo-900',
    emerald: 'bg-emerald-200 text-emerald-900',
    teal: 'bg-teal-200 text-teal-900',
    cyan: 'bg-cyan-200 text-cyan-900',
    sky: 'bg-sky-200 text-sky-900',
    orange: 'bg-orange-200 text-orange-900',
    lime: 'bg-lime-200 text-lime-900',
    yellow: 'bg-yellow-200 text-yellow-900',
  }[accent] || 'bg-amber-200 text-amber-900';
  const accentArrow = {
    amber: 'text-amber-500',
    rose: 'text-rose-500',
    indigo: 'text-indigo-500',
    emerald: 'text-emerald-500',
    teal: 'text-teal-500',
    cyan: 'text-cyan-500',
    sky: 'text-sky-500',
    orange: 'text-orange-500',
    lime: 'text-lime-500',
    yellow: 'text-yellow-500',
  }[accent] || 'text-amber-500';
  const accentSubBg = {
    amber: 'bg-amber-50/40 border-amber-100',
    rose: 'bg-rose-50/40 border-rose-100',
    indigo: 'bg-indigo-50/40 border-indigo-100',
    emerald: 'bg-emerald-50/40 border-emerald-100',
    teal: 'bg-teal-50/40 border-teal-100',
    cyan: 'bg-cyan-50/40 border-cyan-100',
    sky: 'bg-sky-50/40 border-sky-100',
    orange: 'bg-orange-50/40 border-orange-100',
    lime: 'bg-lime-50/40 border-lime-100',
    yellow: 'bg-yellow-50/40 border-yellow-100',
  }[accent] || 'bg-amber-50/40 border-amber-100';
  const testidSuffix = pose.key || 'pose-1';
  return (
    <section className={`rounded-2xl border-2 ${accentRing} bg-white shadow-md overflow-hidden`} data-testid={`w37-${testidSuffix}-card`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${accentBg} px-5 py-4 border-b`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${accentIconBg} flex items-center justify-center shadow`}>
              <Icon size={24} weight="fill" className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {pose.name}
              </h2>
              <p className="text-xs text-slate-700 font-semibold mt-0.5">{pose.description}</p>
            </div>
          </div>
          <PoseBadge tone={accentBadge}>{pose.count_total} lifetime events</PoseBadge>
        </div>
      </div>

      {/* Visual pattern strip */}
      <div className={`px-5 py-4 border-b ${accentSubBg}`}>
        <div className="flex flex-col items-center gap-2 flex-wrap">
          {flowPairs.map(([from, to], idx) => (
            <div key={idx} className="flex items-center justify-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-full bg-white border-2 ${accentChip} flex items-center justify-center font-black text-sm shadow-sm`}>{from}</div>
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">draw N-1</span>
              </div>
              <ArrowRight size={28} weight="bold" className={accentArrow} />
              <div className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-full ${accentChipSolid} border-2 flex items-center justify-center text-white font-black text-sm shadow`}>{to}</div>
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">draw N</span>
              </div>
            </div>
          ))}
          {flowPairs.length > 1 && (
            <span className="mt-1 text-xs text-slate-700 font-semibold">
              ALL {flowPairs.length} crossovers must occur simultaneously (double-match event)
            </span>
          )}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-5">
        <StatCard label="Till 2025" value={pose.count_till_2025} tone="blue" icon={Calendar} testid={`w37-${testidSuffix}-stat-till-2025`} />
        <StatCard label="In 2026" value={pose.count_2026} tone="emerald" icon={Calendar} testid={`w37-${testidSuffix}-stat-2026`} />
        <StatCard label="Avg Bounce-Back" value={pose.avg_bounce_back} suffix="draws" tone="purple" icon={ChartLine} testid={`w37-${testidSuffix}-stat-avg`} />
        <StatCard label="Current Gap" value={pose.current_gap} suffix="draws" tone={gapTone} icon={Clock} testid={`w37-${testidSuffix}-stat-current-gap`} />
        <StatCard label="Total Draws" value={totalDraws} tone="slate" testid={`w37-${testidSuffix}-stat-total-draws`} />
      </div>

      {/* Last event + gap range */}
      <div className="px-5 pb-4 -mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Last event</span>
          <p className="text-sm font-bold text-slate-800 mt-0.5" data-testid={`w37-${testidSuffix}-last-event`}>
            {pose.last_event_draw_number ? (
              <>
                Draw #{pose.last_event_draw_number} · {pose.last_event_date} · number{(pose.last_event_numbers || []).length > 1 ? 's' : ''}{' '}
                {(pose.last_event_numbers || []).map((n, idx) => (
                  <span key={idx} className={`inline-block px-2 py-0.5 rounded font-extrabold ml-1 ${accentNumPill}`}>{n}</span>
                ))}
              </>
            ) : <span className="text-slate-400">No events recorded yet</span>}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Bounce-back range</span>
          <p className="text-sm font-bold text-slate-800 mt-0.5">
            min <span className="text-emerald-700">{pose.min_gap ?? '—'}</span> · max <span className="text-rose-700">{pose.max_gap ?? '—'}</span> · avg <span className="text-purple-700">{pose.avg_bounce_back ?? '—'}</span>
          </p>
        </div>
      </div>
    </section>
  );
};

const EventsTable = ({ events, onExport, title = 'Pose — Event log', accent = 'amber', testidSuffix = 'pose' }) => {
  const [view, setView] = useState('recent'); // recent | all
  const display = view === 'recent' ? events.slice(-20).reverse() : [...events].reverse();
  const tabActive = {
    amber: 'bg-amber-500 text-white',
    rose: 'bg-rose-500 text-white',
    indigo: 'bg-indigo-500 text-white',
    emerald: 'bg-emerald-500 text-white',
    teal: 'bg-teal-500 text-white',
    cyan: 'bg-cyan-500 text-white',
    sky: 'bg-sky-500 text-white',
    orange: 'bg-orange-500 text-white',
  }[accent] || 'bg-amber-500 text-white';
  const headerBg = {
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
    rose: 'bg-rose-50 text-rose-900 border-rose-200',
    indigo: 'bg-indigo-50 text-indigo-900 border-indigo-200',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    teal: 'bg-teal-50 text-teal-900 border-teal-200',
    cyan: 'bg-cyan-50 text-cyan-900 border-cyan-200',
    sky: 'bg-sky-50 text-sky-900 border-sky-200',
    orange: 'bg-orange-50 text-orange-900 border-orange-200',
  }[accent] || 'bg-amber-50 text-amber-900 border-amber-200';
  const numPill = {
    amber: 'bg-amber-200 text-amber-900',
    rose: 'bg-rose-200 text-rose-900',
    indigo: 'bg-indigo-200 text-indigo-900',
    emerald: 'bg-emerald-200 text-emerald-900',
    teal: 'bg-teal-200 text-teal-900',
    cyan: 'bg-cyan-200 text-cyan-900',
    sky: 'bg-sky-200 text-sky-900',
    orange: 'bg-orange-200 text-orange-900',
  }[accent] || 'bg-amber-200 text-amber-900';
  const rowHover = {
    amber: 'hover:bg-amber-50/40',
    rose: 'hover:bg-rose-50/40',
    indigo: 'hover:bg-indigo-50/40',
    emerald: 'hover:bg-emerald-50/40',
    teal: 'hover:bg-teal-50/40',
    cyan: 'hover:bg-cyan-50/40',
    sky: 'hover:bg-sky-50/40',
    orange: 'hover:bg-orange-50/40',
  }[accent] || 'hover:bg-amber-50/40';
  return (
    <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`w37-events-${testidSuffix}`}>
      <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-bold text-slate-800" style={{ fontFamily: 'var(--font-heading)' }}>{title}</h3>
          <PoseBadge tone="slate">{events.length} total</PoseBadge>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg overflow-hidden border border-slate-300">
            <button
              onClick={() => setView('recent')}
              className={`px-3 py-1.5 text-xs font-bold ${view === 'recent' ? tabActive : 'bg-white text-slate-700 hover:bg-slate-100'}`}
              data-testid={`w37-view-recent-${testidSuffix}`}
            >
              Recent 20
            </button>
            <button
              onClick={() => setView('all')}
              className={`px-3 py-1.5 text-xs font-bold border-l border-slate-300 ${view === 'all' ? tabActive : 'bg-white text-slate-700 hover:bg-slate-100'}`}
              data-testid={`w37-view-all-${testidSuffix}`}
            >
              All
            </button>
          </div>
          <Button onClick={onExport} className="bg-[#002FA7] hover:bg-[#001F70] text-white h-8 px-3 text-xs" data-testid={`w37-export-btn-${testidSuffix}`}>
            <DownloadSimple size={14} className="mr-1" /> CSV
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm" data-w37-export-name={title} data-testid={`w37-events-table-${testidSuffix}`}>
          <thead className={`${headerBg} sticky top-0 z-10`}>
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold border-b">#</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold border-b">Prev Draw</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold border-b">Prev Date</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider font-bold border-b">Match #</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold border-b">Curr Draw</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold border-b">Curr Date</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider font-bold border-b">Year</th>
            </tr>
          </thead>
          <tbody>
            {display.map((e, idx) => (
              <tr key={`${e.curr_draw_number}-${idx}`} className={`${rowHover} transition-colors`} data-testid={`w37-${testidSuffix}-row-${e.curr_draw_number}`}>
                <td className="px-3 py-1.5 text-slate-500 font-mono border-b border-slate-100">{display.length - idx}</td>
                <td className="px-3 py-1.5 font-mono text-slate-800 border-b border-slate-100">{e.prev_draw_number}</td>
                <td className="px-3 py-1.5 text-slate-700 border-b border-slate-100">{e.prev_date}</td>
                <td className="px-3 py-1.5 text-center border-b border-slate-100">
                  {(e.numbers || [e.number]).filter(n => n != null).map((n, idx) => (
                    <span key={idx} className={`inline-block px-2 py-0.5 rounded font-extrabold ${numPill} ${idx > 0 ? 'ml-1' : ''}`}>{n}</span>
                  ))}
                </td>
                <td className="px-3 py-1.5 font-mono text-slate-800 border-b border-slate-100">{e.curr_draw_number}</td>
                <td className="px-3 py-1.5 text-slate-700 border-b border-slate-100">{e.curr_date}</td>
                <td className="px-3 py-1.5 text-center text-slate-600 font-bold border-b border-slate-100">{e.year}</td>
              </tr>
            ))}
            {display.length === 0 && (
              <tr data-w37-export-ignore="true"><td colSpan={7} className="px-3 py-6 text-center text-slate-500 italic">No events yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};


const POSE_META = {
  pose_1: { short: 'Pose 1', label: 'LS₁ → B₁',
            bg: 'bg-amber-100 text-amber-900 border-amber-300' },
  pose_2: { short: 'Pose 2', label: 'LS₂ → B₁',
            bg: 'bg-rose-100 text-rose-900 border-rose-300' },
  pose_3: { short: 'Pose 3', label: 'LS₁+LS₂ → B₁+B₂',
            bg: 'bg-indigo-100 text-indigo-900 border-indigo-300' },
  pose_4: { short: 'Pose 4', label: 'LS₁ → B₂',
            bg: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  pose_5: { short: 'Pose 5', label: 'LS₂ → B₂',
            bg: 'bg-teal-100 text-teal-900 border-teal-300' },
  pose_6: { short: 'Pose 6', label: 'LS₁+LS₂ → B₁',
            bg: 'bg-cyan-100 text-cyan-900 border-cyan-300' },
  pose_7: { short: 'Pose 7', label: 'LS₁+LS₂ → B₂',
            bg: 'bg-sky-100 text-sky-900 border-sky-300' },
  pose_8: { short: 'Pose 8', label: 'LS₂−LS1 → B₁',
            bg: 'bg-orange-100 text-orange-900 border-orange-300' },
  pose_9: { short: 'Pose 9', label: 'B_K(N-2) + B_K(N-1) → B_K',
            bg: 'bg-lime-100 text-lime-900 border-lime-300' },
  pose_10: { short: 'Pose 10', label: '|B_K(N-1) − B_K(N-2)| → B_K',
            bg: 'bg-yellow-100 text-yellow-900 border-yellow-300' },
  pose_11: { short: 'Pose 11', label: 'Diagonal Pickup',
            bg: 'bg-violet-100 text-violet-900 border-violet-300' },
  pose_12: { short: 'Pose 12', label: 'B_K(N-2) = B_K(N) — Same-Ball Echo',
            bg: 'bg-sky-100 text-sky-900 border-sky-300' },
};

const intensityClass = (pct) => {
  if (pct == null) return 'bg-slate-50 text-slate-400';
  if (pct >= 35) return 'bg-emerald-300 text-emerald-900 font-black ring-2 ring-emerald-500';
  if (pct >= 25) return 'bg-emerald-200 text-emerald-900 font-bold';
  if (pct >= 15) return 'bg-emerald-100 text-emerald-800 font-semibold';
  if (pct >= 5)  return 'bg-amber-50 text-amber-800';
  return 'bg-slate-50 text-slate-500';
};

// Symptom Progression Matrix — shows P(pose_j fires within K draws | pose_i fired)
const SymptomProgression = ({ progression }) => {
  if (!progression) return null;
  const K = progression.lookahead_draws ?? 5;
  const transitions = progression.transitions || {};
  const live = progression.live_status;
  const orderedKeys = ['pose_1', 'pose_2', 'pose_3', 'pose_4', 'pose_5', 'pose_6', 'pose_7', 'pose_8', 'pose_9', 'pose_10', 'pose_11', 'pose_12'].filter(k => transitions[k]);

  return (
    <section className="rounded-2xl border-2 border-fuchsia-300 bg-white shadow-md overflow-hidden" data-testid="w37-symptom-progression">
      <div className="bg-gradient-to-r from-fuchsia-100 via-purple-100 to-indigo-100 px-5 py-4 border-b border-fuchsia-200">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-fuchsia-500 to-indigo-600 flex items-center justify-center shadow">
            <ChartLine size={24} weight="fill" className="text-white" />
          </div>
          <div>
            <h2 className="text-xl font-black text-slate-900 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Symptom Progression Matrix
            </h2>
            <p className="text-xs text-slate-700 font-semibold mt-0.5">
              When Pose <em>X</em> fires today, how often does Pose <em>Y</em> follow within the next {K} draws? The doctor&apos;s diagnostic chart.
            </p>
          </div>
        </div>
      </div>

      {live?.most_recent_event && (
        <div className="px-5 py-4 bg-fuchsia-50/40 border-b border-fuchsia-100" data-testid="w37-live-watch">
          <p className="text-[10px] uppercase tracking-wider font-extrabold text-fuchsia-700">Most recent symptom</p>
          <p className="text-sm font-bold text-slate-800 mt-1" data-testid="w37-live-most-recent">
            <span className={`inline-block px-2 py-0.5 rounded font-extrabold mr-2 ${POSE_META[live.most_recent_event.pose]?.bg || ''}`}>
              {POSE_META[live.most_recent_event.pose]?.short}
            </span>
            {live.most_recent_event.pose_name} · Draw #{live.most_recent_event.draw_number} · {live.most_recent_event.date}
            <span className="ml-2 text-slate-500">({live.most_recent_event.draws_ago} draws ago)</span>
            {(live.most_recent_event.numbers || []).map((n, idx) => (
              <span key={idx} className="inline-block px-2 py-0.5 rounded bg-slate-200 text-slate-900 font-extrabold ml-1">{n}</span>
            ))}
          </p>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {(live.next_likely || []).map((r) => (
              <div
                key={r.pose}
                className={`rounded-lg border px-3 py-2 ${r.is_self ? 'border-slate-300 bg-slate-50' : 'border-fuchsia-300 bg-white'}`}
                data-testid={`w37-next-likely-${r.pose}`}
                data-w37-export-card-row="Next Likely Poses" data-w37-export-context={POSE_META[r.pose]?.short}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-extrabold ${POSE_META[r.pose]?.bg || ''}`}>{POSE_META[r.pose]?.short}</span>
                  {r.is_self && <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">echo</span>}
                </div>
                <p className="text-sm font-bold text-slate-800 mt-1.5">
                  <span className="text-lg font-black text-fuchsia-700">{r.pct ?? 0}%</span>
                  <span className="text-xs font-semibold text-slate-500"> within {K} draws</span>
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  avg lag {r.avg_lag ?? '—'} · {r.count} hits
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-5">
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">
          Transition matrix · row = symptom that fires → column = symptom that follows within {K} draws
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="w37-transition-matrix">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500 border border-slate-200 bg-slate-50">From&nbsp;\&nbsp;To</th>
                {orderedKeys.map((k) => (
                  <th key={k} className={`px-3 py-2 text-center text-[11px] font-extrabold border border-slate-200 ${POSE_META[k]?.bg || ''}`}>
                    {POSE_META[k]?.short} <span className="font-mono opacity-70">({POSE_META[k]?.label})</span>
                  </th>
                ))}
                <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider font-bold text-slate-500 border border-slate-200 bg-slate-50">Triggers</th>
              </tr>
            </thead>
            <tbody>
              {orderedKeys.map((src) => {
                const srcRow = transitions[src] || {};
                return (
                  <tr key={src}>
                    <th className={`px-3 py-2 text-left text-[11px] font-extrabold border border-slate-200 ${POSE_META[src]?.bg || ''}`}>
                      {POSE_META[src]?.short} <span className="font-mono opacity-70">({POSE_META[src]?.label})</span>
                    </th>
                    {orderedKeys.map((tgt) => {
                      const s = srcRow.to?.[tgt] || {};
                      const pct = s.pct ?? 0;
                      const cls = intensityClass(pct);
                      const isSelf = src === tgt;
                      return (
                        <td key={tgt} className={`px-3 py-2 text-center font-mono border border-slate-200 ${cls}`} data-testid={`w37-cell-${src}-${tgt}`}
                            title={`${s.count ?? 0} of ${s.total_triggers ?? 0} ${POSE_META[src]?.short} triggers ${isSelf ? 'recurred' : `were followed by ${POSE_META[tgt]?.short}`} within ${K} draws. Avg lag ${s.avg_lag ?? '—'} (min ${s.min_lag ?? '—'} / max ${s.max_lag ?? '—'})`}>
                          <div className="text-base font-black">{pct}%</div>
                          <div className="text-[10px] font-bold opacity-80">{s.count ?? 0}/{s.total_triggers ?? 0}</div>
                          <div className="text-[10px] opacity-70">lag {s.avg_lag ?? '—'}</div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center font-bold border border-slate-200 bg-slate-50 text-slate-700">
                      {srcRow.total_triggers ?? 0}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          <strong>Reading the table</strong>: row = symptom that fired, column = symptom that follows. Diagonal = &ldquo;echo&rdquo; (same pose recurring).
          Each cell shows the % of source-triggers followed by the column pose within {K} draws, the raw count, and the average lag.
          A green ring means ≥ 35% — a very strong follow-on signal.
        </p>
      </div>
    </section>
  );
};


// ---------- Pose 11 — Diagonal Pickup ----------
// Custom card because the visual is a "staircase" mini-table rather than a
// simple flow strip, and events span multiple prior draws (depth 3 or 4).
const DiagonalCard = ({ pose, totalDraws }) => {
  if (!pose) return null;
  const gapTone = pose.current_gap >= (pose.avg_bounce_back || 0) && pose.avg_bounce_back ? 'rose' : 'emerald';
  return (
    <section className="rounded-2xl border-2 border-violet-300 bg-white shadow-md overflow-hidden" data-testid="w37-pose-11-card">
      <div className="bg-gradient-to-r from-violet-100 via-purple-100 to-fuchsia-100 px-5 py-4 border-b border-violet-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow">
              <Stairs size={24} weight="fill" className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                Pose 11 — Diagonal Pickup
              </h2>
              <p className="text-xs text-slate-700 font-semibold mt-0.5">{pose.description}</p>
            </div>
          </div>
          <PoseBadge tone="slate">{pose.count_total} lifetime events</PoseBadge>
        </div>
      </div>

      {/* Visual: schematic staircase showing the diagonal */}
      <div className="px-5 py-4 bg-violet-50/40 border-b border-violet-100">
        <div className="text-[11px] uppercase tracking-wider font-bold text-violet-700 mb-2 text-center">
          Diagonal Match Rule — B(K) of draw N == B(K+1) of draw N-K
        </div>
        <div className="flex justify-center">
          <table className="border border-violet-300 rounded overflow-hidden text-xs">
            <thead className="bg-violet-100">
              <tr>
                <th className="px-2 py-1 text-violet-800 font-bold border-r border-violet-200">Draw</th>
                <th className="px-2 py-1 text-violet-800 font-bold">B1</th>
                <th className="px-2 py-1 text-violet-800 font-bold">B2</th>
                <th className="px-2 py-1 text-violet-800 font-bold">B3</th>
                <th className="px-2 py-1 text-violet-800 font-bold">B4</th>
                <th className="px-2 py-1 text-violet-800 font-bold">B5</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              <tr>
                <th className="px-2 py-1 text-slate-700 border-r border-violet-200 bg-violet-50">N</th>
                <td className="px-3 py-1 bg-violet-300 text-violet-900 font-extrabold text-center">A</td>
                <td className="px-3 py-1 bg-violet-300 text-violet-900 font-extrabold text-center">B</td>
                <td className="px-3 py-1 bg-violet-300 text-violet-900 font-extrabold text-center">C</td>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
              </tr>
              <tr>
                <th className="px-2 py-1 text-slate-700 border-r border-violet-200 bg-violet-50">N-1</th>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
                <td className="px-3 py-1 bg-violet-200 text-violet-900 font-extrabold text-center">A</td>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
              </tr>
              <tr>
                <th className="px-2 py-1 text-slate-700 border-r border-violet-200 bg-violet-50">N-2</th>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
                <td className="px-3 py-1 bg-violet-200 text-violet-900 font-extrabold text-center">B</td>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
              </tr>
              <tr>
                <th className="px-2 py-1 text-slate-700 border-r border-violet-200 bg-violet-50">N-3</th>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
                <td className="px-3 py-1 bg-violet-200 text-violet-900 font-extrabold text-center">C</td>
                <td className="px-3 py-1 text-slate-400 text-center">·</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-center text-slate-600 mt-2">
          Event fires when at least <strong>3 contiguous diagonal matches</strong> exist (max depth 4 since B5 has no diagonal predecessor).
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-5">
        <StatCard label="Till 2025" value={pose.count_till_2025} tone="blue" icon={Calendar} testid="w37-pose-11-stat-till-2025" />
        <StatCard label="In 2026" value={pose.count_2026} tone="emerald" icon={Calendar} testid="w37-pose-11-stat-2026" />
        <StatCard label="Avg Bounce-Back" value={pose.avg_bounce_back ?? '—'} suffix={pose.avg_bounce_back ? 'draws' : ''} tone="purple" icon={ChartLine} testid="w37-pose-11-stat-avg" />
        <StatCard label="Current Gap" value={pose.current_gap} suffix="draws" tone={gapTone} icon={Clock} testid="w37-pose-11-stat-current-gap" />
        <StatCard label="Total Draws" value={totalDraws} tone="slate" testid="w37-pose-11-stat-total-draws" />
      </div>

      {/* Depth breakdown + last event */}
      <div className="px-5 pb-4 -mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Depth distribution</span>
          <p className="text-sm font-bold text-slate-800 mt-0.5">
            depth 3: <span className="text-violet-700">{pose.by_depth?.['3'] ?? 0}</span> · depth 4: <span className="text-violet-700">{pose.by_depth?.['4'] ?? 0}</span>
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Last event</span>
          <p className="text-sm font-bold text-slate-800 mt-0.5" data-testid="w37-pose-11-last-event">
            {pose.last_event_draw_number ? (
              <>
                Draw #{pose.last_event_draw_number} · {pose.last_event_date} · depth {pose.last_event_depth} ·
                {(pose.last_event_numbers || []).map((n, idx) => (
                  <span key={idx} className="inline-block px-2 py-0.5 rounded bg-violet-200 text-violet-900 font-extrabold ml-1">{n}</span>
                ))}
              </>
            ) : <span className="text-slate-400">No events recorded yet</span>}
          </p>
        </div>
      </div>
    </section>
  );
};

// Pose 4 event log — each event shows the diagonal staircase as a mini-grid
const DiagonalEventsList = ({ events, onExport }) => {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="w37-events-pose-11">
      <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-bold text-slate-800" style={{ fontFamily: 'var(--font-heading)' }}>Pose 11 — Diagonal Pickup event log</h3>
          <PoseBadge tone="slate">{events.length} total</PoseBadge>
        </div>
        <Button onClick={onExport} className="bg-[#002FA7] hover:bg-[#001F70] text-white h-8 px-3 text-xs" data-testid="w37-export-btn-pose-11">
          <DownloadSimple size={14} className="mr-1" /> CSV
        </Button>
      </div>
      <div className="overflow-y-auto max-h-[70vh] divide-y divide-slate-100">
        {events.length === 0 ? (
          <div className="px-5 py-6 text-center text-slate-500 italic">No diagonal-pickup events yet.</div>
        ) : (
          [...events].reverse().map((e, idx) => (
            <div key={`${e.curr_draw_number}-${idx}`} className="px-5 py-4 hover:bg-violet-50/30 transition-colors" data-testid={`w37-pose-11-row-${e.curr_draw_number}`} data-w37-export-event="true">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Event #{events.length - idx}</span>
                <span className="font-mono text-slate-800 font-bold">Draw #{e.curr_draw_number}</span>
                <span className="text-slate-600">{e.curr_date}</span>
                <span className="inline-block px-2 py-0.5 rounded bg-violet-200 text-violet-900 font-extrabold text-xs">depth {e.depth}</span>
                <span className="text-slate-600 text-xs">Year {e.year}</span>
                <span className="ml-auto flex items-center gap-1">
                  {(e.numbers || []).map((n, i) => (
                    <span key={i} className="inline-block px-2 py-0.5 rounded bg-violet-100 text-violet-900 font-extrabold text-xs">{n}</span>
                  ))}
                </span>
              </div>
              {/* Mini staircase grid */}
              <div className="overflow-x-auto">
                <table className="text-xs border border-violet-200 rounded" data-w37-export-name="Pose 11 — Diagonal Pickup event log" data-testid={`w37-diagonal-table-${e.curr_draw_number}-${idx}`}>
                  <thead className="bg-violet-50">
                    <tr>
                      <th className="px-2 py-1 text-left text-violet-800 font-bold border-r border-violet-200">Date</th>
                      <th className="px-2 py-1 text-violet-800 font-bold">B1</th>
                      <th className="px-2 py-1 text-violet-800 font-bold">B2</th>
                      <th className="px-2 py-1 text-violet-800 font-bold">B3</th>
                      <th className="px-2 py-1 text-violet-800 font-bold">B4</th>
                      <th className="px-2 py-1 text-violet-800 font-bold">B5</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {/* Current draw row */}
                    <tr>
                      <th className="px-2 py-1 text-slate-700 border-r border-violet-200 bg-violet-50 text-left">{e.curr_date}</th>
                      {[1,2,3,4,5].map((b) => {
                        const m = (e.matches || []).find(mm => mm.curr_ball === `ball_${b}`);
                        return (
                          <td key={b} className={`px-3 py-1 text-center border border-violet-100 ${m ? 'bg-violet-300 text-violet-950 font-extrabold' : 'text-slate-400'}`}>
                            {m ? m.value : '·'}
                          </td>
                        );
                      })}
                    </tr>
                    {/* Predecessor rows */}
                    {(e.matches || []).map((m, mi) => (
                      <tr key={mi}>
                        <th className="px-2 py-1 text-slate-700 border-r border-violet-200 bg-violet-50 text-left">{m.prev_date}</th>
                        {[1,2,3,4,5].map((b) => {
                          const isMatch = m.prev_ball === `ball_${b}`;
                          return (
                            <td key={b} className={`px-3 py-1 text-center border border-violet-100 ${isMatch ? 'bg-violet-200 text-violet-900 font-extrabold' : 'text-slate-300'}`}>
                              {isMatch ? m.value : '·'}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
};


// ---------- 3-Draw Triplet Card (Pose 9 / 10) ----------
// Pose 9 = a + b = c (same-ball sum), Pose 10 = |b − a| = c (same-ball diff).
const TripletCard = ({ pose, totalDraws, accent, operator, testidSuffix, formulaMode = 'triplet' }) => {
  if (!pose) return null;
  const gapTone = pose.current_gap >= (pose.avg_bounce_back || 0) && pose.avg_bounce_back ? 'rose' : 'emerald';
  const accentBg = {
    lime: 'from-lime-100 via-green-100 to-emerald-100 border-lime-200',
    yellow: 'from-yellow-100 via-amber-100 to-orange-100 border-yellow-300',
    sky: 'from-sky-100 via-blue-100 to-indigo-100 border-sky-200',
  }[accent] || 'from-lime-100 via-green-100 to-emerald-100 border-lime-200';
  const accentIconBg = {
    lime: 'from-lime-500 to-green-600',
    yellow: 'from-yellow-500 to-amber-600',
    sky: 'from-sky-500 to-blue-600',
  }[accent] || 'from-lime-500 to-green-600';
  const accentRing = {
    lime: 'border-lime-300',
    yellow: 'border-yellow-400',
    sky: 'border-sky-300',
  }[accent] || 'border-lime-300';
  const accentSubBg = {
    lime: 'bg-lime-50/40 border-lime-100',
    yellow: 'bg-yellow-50/40 border-yellow-100',
    sky: 'bg-sky-50/40 border-sky-100',
  }[accent] || 'bg-lime-50/40 border-lime-100';
  const accentBadge = { lime: 'emerald', yellow: 'amber', sky: 'blue' }[accent] || 'emerald';
  const numPill = {
    lime: 'bg-lime-200 text-lime-900',
    yellow: 'bg-yellow-200 text-yellow-900',
    sky: 'bg-sky-200 text-sky-900',
  }[accent] || 'bg-lime-200 text-lime-900';
  return (
    <section className={`rounded-2xl border-2 ${accentRing} bg-white shadow-md overflow-hidden`} data-testid={`w37-${testidSuffix}-card`}>
      <div className={`bg-gradient-to-r ${accentBg} px-5 py-4 border-b`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${accentIconBg} flex items-center justify-center shadow`}>
              <Sparkle size={24} weight="fill" className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {pose.name}
              </h2>
              <p className="text-xs text-slate-700 font-semibold mt-0.5">{pose.description}</p>
            </div>
          </div>
          <PoseBadge tone={accentBadge}>{pose.count_total} lifetime events</PoseBadge>
        </div>
      </div>

      {/* Formula schematic */}
      <div className={`px-5 py-4 border-b ${accentSubBg}`}>
        <div className="flex items-center justify-center gap-3 flex-wrap text-sm font-bold text-slate-700">
          <span className={`inline-block px-3 py-1.5 rounded ${numPill}`}>B<sub>K</sub>(N-2)</span>
          {formulaMode === 'echo' ? (
            <>
              <span className="text-lg font-black">=</span>
              <span className={`inline-block px-3 py-1.5 rounded ${numPill}`}>B<sub>K</sub>(N)</span>
              <span className="ml-2 text-xs text-slate-500 font-semibold italic">(N-1 ignored)</span>
            </>
          ) : (
            <>
              <span className="text-lg font-black">{operator}</span>
              <span className={`inline-block px-3 py-1.5 rounded ${numPill}`}>B<sub>K</sub>(N-1)</span>
              <span className="text-lg font-black">=</span>
              <span className={`inline-block px-3 py-1.5 rounded ${numPill}`}>B<sub>K</sub>(N)</span>
            </>
          )}
          <span className="ml-2 text-xs text-slate-600 font-semibold">K = 1..5 (any ball position)</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-5">
        <StatCard label="Till 2025" value={pose.count_till_2025} tone="blue" icon={Calendar} testid={`w37-${testidSuffix}-stat-till-2025`} />
        <StatCard label="In 2026" value={pose.count_2026} tone="emerald" icon={Calendar} testid={`w37-${testidSuffix}-stat-2026`} />
        <StatCard label="Avg Bounce-Back" value={pose.avg_bounce_back ?? '—'} suffix={pose.avg_bounce_back ? 'draws' : ''} tone="purple" icon={ChartLine} testid={`w37-${testidSuffix}-stat-avg`} />
        <StatCard label="Current Gap" value={pose.current_gap} suffix="draws" tone={gapTone} icon={Clock} testid={`w37-${testidSuffix}-stat-current-gap`} />
        <StatCard label="Total Draws" value={totalDraws} tone="slate" testid={`w37-${testidSuffix}-stat-total-draws`} />
      </div>

      {/* Per-ball breakdown table — each ball position analysed separately.
          Pose 9 (sum) and Pose 10 (diff) are naturally skewed toward lower ball
          positions because B1+B2 small enough to land in the 1..50 range more
          often than larger positions. Per-ball stats expose this clearly. */}
      <div className="px-5 pb-4 -mt-2">
        <div className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
          <div className={`px-3 py-1.5 border-b border-slate-200 ${accentSubBg}`}>
            <span className="font-extrabold text-slate-600 uppercase tracking-wider text-[10px]">
              Per-ball breakdown — each ball position analysed individually
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid={`w37-${testidSuffix}-per-ball-table`}>
              <thead className={`${accent === 'yellow' ? 'bg-yellow-100 text-yellow-900' : accent === 'sky' ? 'bg-sky-100 text-sky-900' : 'bg-lime-100 text-lime-900'} border-b`}>
                <tr>
                  <th className="px-2 py-1.5 text-left font-extrabold uppercase tracking-wider text-[10px]">Ball</th>
                  <th className="px-2 py-1.5 text-right font-extrabold uppercase tracking-wider text-[10px]">Total</th>
                  <th className="px-2 py-1.5 text-right font-extrabold uppercase tracking-wider text-[10px]">Till 2025</th>
                  <th className="px-2 py-1.5 text-right font-extrabold uppercase tracking-wider text-[10px]">In 2026</th>
                  <th className="px-2 py-1.5 text-right font-extrabold uppercase tracking-wider text-[10px]">Avg Bounce</th>
                  <th className="px-2 py-1.5 text-right font-extrabold uppercase tracking-wider text-[10px]">Current Gap</th>
                  <th className="px-2 py-1.5 text-left font-extrabold uppercase tracking-wider text-[10px]">Last Event</th>
                </tr>
              </thead>
              <tbody>
                {[1,2,3,4,5].map((b) => {
                  const bd = pose.by_ball_detailed?.[String(b)] || pose.by_ball_detailed?.[b] || {};
                  const bCount = bd.count_total ?? ((pose.by_ball?.[String(b)] ?? pose.by_ball?.[b]) || 0);
                  const bGapTone = bd.avg_bounce_back && bd.current_gap >= bd.avg_bounce_back
                    ? 'text-rose-700 font-extrabold' : 'text-emerald-700 font-extrabold';
                  return (
                    <tr key={b} className="border-b last:border-b-0 border-slate-100" data-testid={`w37-${testidSuffix}-per-ball-row-${b}`}>
                      <td className="px-2 py-1.5">
                        <span className={`inline-block px-2 py-0.5 rounded font-extrabold ${numPill}`}>B{b}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-bold text-slate-800">{bCount}</td>
                      <td className="px-2 py-1.5 text-right text-slate-600">{bd.count_till_2025 ?? '—'}</td>
                      <td className="px-2 py-1.5 text-right text-emerald-700 font-bold">{bd.count_2026 ?? '—'}</td>
                      <td className="px-2 py-1.5 text-right text-slate-700">{bd.avg_bounce_back ?? '—'}{bd.avg_bounce_back ? <span className="text-[10px] text-slate-500 ml-0.5">draws</span> : null}</td>
                      <td className={`px-2 py-1.5 text-right ${bd.last_event_draw_number ? bGapTone : 'text-slate-400'}`}>
                        {bd.last_event_draw_number ? <>{bd.current_gap}<span className="text-[10px] text-slate-500 ml-0.5">draws</span></> : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-slate-700">
                        {bd.last_event_draw_number ? (
                          <span>
                            #{bd.last_event_draw_number} · {bd.last_event_date} ·{' '}
                            {formulaMode === 'echo' ? (
                              <>
                                <span className={`inline-block px-1.5 py-0.5 rounded font-extrabold ${numPill}`}>{bd.last_event_value_n2}</span>
                                <span className="text-slate-500 mx-1">→</span>
                                <span className={`inline-block px-1.5 py-0.5 rounded font-extrabold ${numPill}`}>{bd.last_event_value}</span>
                              </>
                            ) : (
                              <>
                                <span className="text-slate-500">{bd.last_event_value_n2}{` ${operator} `}{bd.last_event_value_n1} =</span>{' '}
                                <span className={`inline-block px-1.5 py-0.5 rounded font-extrabold ${numPill}`}>{bd.last_event_value}</span>
                              </>
                            )}
                          </span>
                        ) : <span className="text-slate-400">No events</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Last event (overall) — single line summary */}
      <div className="px-5 pb-4 text-xs">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Most recent event (any ball)</span>
          <p className="text-sm font-bold text-slate-800 mt-0.5" data-testid={`w37-${testidSuffix}-last-event`}>
            {pose.last_event_draw_number ? (
              <>
                Draw #{pose.last_event_draw_number} · {pose.last_event_date} · Ball {pose.last_event_ball} ·
                <span className={`inline-block px-2 py-0.5 rounded font-extrabold ml-1 ${numPill}`}>{pose.last_event_number}</span>
              </>
            ) : <span className="text-slate-400">No events recorded yet</span>}
          </p>
        </div>
      </div>
    </section>
  );
};

const TripletEventsList = ({ events, onExport, title, accent, testidSuffix, operatorLabel }) => {
  const [view, setView] = useState('recent');
  const display = view === 'recent' ? events.slice(-20).reverse() : [...events].reverse();
  const tabActive = {
    lime: 'bg-lime-500 text-white',
    yellow: 'bg-yellow-500 text-white',
    sky: 'bg-sky-500 text-white',
  }[accent] || 'bg-lime-500 text-white';
  const headerBg = {
    lime: 'bg-lime-50 text-lime-900 border-lime-200',
    yellow: 'bg-yellow-50 text-yellow-900 border-yellow-200',
    sky: 'bg-sky-50 text-sky-900 border-sky-200',
  }[accent] || 'bg-lime-50 text-lime-900 border-lime-200';
  const numPill = {
    lime: 'bg-lime-200 text-lime-900',
    yellow: 'bg-yellow-200 text-yellow-900',
    sky: 'bg-sky-200 text-sky-900',
  }[accent] || 'bg-lime-200 text-lime-900';
  const rowHover = {
    lime: 'hover:bg-lime-50/40',
    yellow: 'hover:bg-yellow-50/40',
    sky: 'hover:bg-sky-50/40',
  }[accent] || 'hover:bg-lime-50/40';
  return (
    <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`w37-events-${testidSuffix}`}>
      <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-bold text-slate-800" style={{ fontFamily: 'var(--font-heading)' }}>{title}</h3>
          <PoseBadge tone="slate">{events.length} total</PoseBadge>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg overflow-hidden border border-slate-300">
            <button onClick={() => setView('recent')} className={`px-3 py-1.5 text-xs font-bold ${view === 'recent' ? tabActive : 'bg-white text-slate-700 hover:bg-slate-100'}`} data-testid={`w37-view-recent-${testidSuffix}`}>Recent 20</button>
            <button onClick={() => setView('all')} className={`px-3 py-1.5 text-xs font-bold border-l border-slate-300 ${view === 'all' ? tabActive : 'bg-white text-slate-700 hover:bg-slate-100'}`} data-testid={`w37-view-all-${testidSuffix}`}>All</button>
          </div>
          <Button onClick={onExport} className="bg-[#002FA7] hover:bg-[#001F70] text-white h-8 px-3 text-xs" data-testid={`w37-export-btn-${testidSuffix}`}>
            <DownloadSimple size={14} className="mr-1" /> CSV
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm" data-w37-export-name={title} data-testid={`w37-events-table-${testidSuffix}`}>
          <thead className={`${headerBg} sticky top-0 z-10`}>
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold border-b">#</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider font-bold border-b">Ball</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold border-b">N-2 Date</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider font-bold border-b">N-2 Val</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold border-b">N-1 Date</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider font-bold border-b">N-1 Val</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold border-b">Curr Date</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider font-bold border-b">Match</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-wider font-bold border-b">Year</th>
            </tr>
          </thead>
          <tbody>
            {display.map((e, idx) => (
              <tr key={`${e.draw_curr}-${e.ball}-${idx}`} className={`${rowHover} transition-colors`} data-testid={`w37-${testidSuffix}-row-${e.draw_curr}-${e.ball}`}>
                <td className="px-3 py-1.5 text-slate-500 font-mono border-b border-slate-100">{display.length - idx}</td>
                <td className="px-3 py-1.5 text-center font-extrabold text-slate-800 border-b border-slate-100">B{e.ball}</td>
                <td className="px-3 py-1.5 text-slate-700 border-b border-slate-100">{e.date_n2}</td>
                <td className="px-3 py-1.5 text-center font-mono text-slate-800 border-b border-slate-100">{e.value_n2}</td>
                <td className="px-3 py-1.5 text-slate-700 border-b border-slate-100">{e.date_n1}</td>
                <td className="px-3 py-1.5 text-center font-mono text-slate-800 border-b border-slate-100">{e.value_n1}</td>
                <td className="px-3 py-1.5 text-slate-700 border-b border-slate-100">{e.date_curr}</td>
                <td className="px-3 py-1.5 text-center border-b border-slate-100">
                  <span className={`inline-block px-2 py-0.5 rounded font-extrabold ${numPill}`} title={`${e.value_n2} ${operatorLabel} ${e.value_n1} = ${e.value_curr}`}>{e.value_curr}</span>
                </td>
                <td className="px-3 py-1.5 text-center text-slate-600 font-bold border-b border-slate-100">{e.year}</td>
              </tr>
            ))}
            {display.length === 0 && (
              <tr data-w37-export-ignore="true"><td colSpan={9} className="px-3 py-6 text-center text-slate-500 italic">No events yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

// ---------- page ----------
/* ================================================================
 * DrawReferenceLegend — decodes the naming convention used across
 * every pose description. Renders three side-by-side chips:
 *   • Recent draw (N)            → B1..B5, LS1, LS2
 *   • Previous draw (N-1)        → PB1..PB5, PLS1, PLS2
 *   • Previous-to-previous (N-2) → P2B1..P2B5, P2LS1, P2LS2
 * Values come from the backend so the legend always reflects live data.
 * ================================================================ */
function DrawReferenceLegend({ recent, prev, prev2 }) {
  if (!recent && !prev && !prev2) return null;
  const line = (label, draw, prefix) => {
    if (!draw) return null;
    const cells = [
      ['B1', draw.ball_1], ['B2', draw.ball_2], ['B3', draw.ball_3],
      ['B4', draw.ball_4], ['B5', draw.ball_5],
      ['LS1', draw.lucky_star_1], ['LS2', draw.lucky_star_2],
    ];
    return (
      <div className="flex items-baseline flex-wrap gap-1.5" data-testid={`w37-draw-ref-${prefix || 'recent'}`} data-w37-export-card-row="Draw References" data-w37-export-context={label}>
        <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider min-w-[110px]">{label}</span>
        <span className="text-[10px] font-mono text-slate-500">{draw.draw_date} · #{draw.draw_number}</span>
        {cells.map(([lbl, v]) => (
          <span key={lbl} className="inline-flex items-baseline gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 font-mono text-[10px]">
            <span className="text-amber-800 font-black">{prefix || ''}{lbl}</span>
            <span className="text-amber-500">=</span>
            <span className="text-slate-900 font-black">{v ?? '—'}</span>
          </span>
        ))}
      </div>
    );
  };
  return (
    <div className="px-5 py-2.5 bg-slate-50 border-b border-amber-200" data-testid="w37-draw-reference-legend">
      <div className="text-[11px] font-black text-slate-800 uppercase tracking-wider mb-1.5">
        Draw-reference naming used in the descriptions below
      </div>
      <div className="space-y-1.5">
        {line('Recent (N)',           recent, '')}
        {line('Previous (N-1)',       prev,   'P')}
        {line('Prev-to-Prev (N-2)',   prev2,  'P2')}
      </div>
    </div>
  );
}

/* ================================================================
 * PosesSummaryTable — a one-glance dashboard of ALL W37 poses.
 * Columns: S.No · Pose · Avg Bounce-Back · Current Gap · Avg/year (till 2025) · 2026 count.
 * Highlights "due" poses (current gap ≥ avg bounce-back).
 * Auto-refreshes with the rest of the page on every draw update.
 * ================================================================ */
// Structural range map — LS1 ∈ [1,11], LS2 ∈ [2,12]. Main balls are 5
// distinct values from [1,50] sorted ascending, so each position has a
// tighter structural bound than the global [1,50]: position i (1-indexed)
// can never be lower than i or higher than 50-(5-i) — e.g. Ball 4 needs
// Ball 5 to be strictly greater, so Ball 4 can never reach 50.
// Shared between the row-composer's "Not possible" tag and the Prediction
// Card's "isValid" flag so out-of-range projections show as "—" everywhere.
const POSE_VALID_RANGE = {
  lucky_star_1: [1, 11],
  lucky_star_2: [2, 12],
  ball_1: [1, 46], ball_2: [2, 47], ball_3: [3, 48], ball_4: [4, 49], ball_5: [5, 50],
};
const poseInRange = (field, val) => {
  if (val == null) return false;
  const r = POSE_VALID_RANGE[field];
  if (!r) return true;
  return val >= r[0] && val <= r[1];
};

function PosesSummaryTable({ poses, nYearsTill2025, recentDraw, previousDraw, previousPreviousDraw, neverInMonthPoses, neverInMonthMeta }) {
  const mobileTablesRef = useMobileTableCards();
  // Extract "Pose N" display number from the backend-provided `name` field so
  // that renumbering only requires backend changes. Falls back to Infinity if
  // absent so unnumbered poses (e.g. "Pose X", "Pose Y") sort to the end.
  const displayNum = (name) => {
    const m = /Pose\s+(\d+(?:\.\d+)?)/.exec(name || '');
    return m ? parseFloat(m[1]) : Infinity;
  };
  // Extract a readable label for the S.No. cell — numeric when possible,
  // otherwise the first token after "Pose " (e.g. "X", "Y").
  const displayLabel = (name) => {
    const numMatch = /Pose\s+(\d+(?:\.\d+)?)/.exec(name || '');
    if (numMatch) return numMatch[1];
    const wordMatch = /Pose\s+(\S+)/.exec(name || '');
    return wordMatch ? wordMatch[1] : '—';
  };

  // Parent-child grouping:
  //  • Triplet poses (Same-Ball Sum / Diff / Echo) expose `by_ball_detailed`
  //    for balls 1..5 → sub-rows labelled `<parent>.1..<parent>.5`.
  //  • All other poses render as a single row.
  const TRIPLET_KEYS = new Set(['pose_9', 'pose_10', 'pose_12']);
  const rows = [];

  Object.entries(poses || {}).forEach(([pk, p]) => {
    if (!p || typeof p !== 'object' || Array.isArray(p) || typeof p.name !== 'string') return;
    const parentNum = displayNum(p.name);
    const parentLabel = displayLabel(p.name);
    if (TRIPLET_KEYS.has(pk)) {
      // Triplet parents (Same-Ball Sum / Diff / Echo) are aggregate rows
      // across balls 1..5. Now that every ball has its OWN group, the parent
      // summary would land in the un-targeted "Other" bucket which is
      // confusing. Skip the parent row entirely and promote each per-ball
      // child to a first-class row in its matching Ball K group instead.
      for (let b = 1; b <= 5; b++) {
        const bd = (p.by_ball_detailed || {})[String(b)];
        if (!bd) continue;
        const subLabel = pk === 'pose_9' ? 'sum'
          : pk === 'pose_10' ? 'diff'
          : 'echo';
        // Compose the actual prediction formula so the row header reads
        // e.g. "Same-Ball Sum · Ball 1 : 10 + 12 = 22" rather than just the
        // abstract ball label. Fall back gracefully when source values or
        // predicted value are missing.
        let childName = `${p.name} · Ball ${b}`;
        if (bd.predicted_source_n1 != null && bd.predicted_source_n2 != null) {
          const op = bd.predicted_operator || '+';
          if (op === 'echo') {
            childName += ` = B_${b}(N-2) → ${bd.predicted_source_n2}${bd.predicted_value != null ? ` → ${bd.predicted_value}` : ''}`;
          } else {
            const rhs = bd.predicted_value != null ? ` = ${bd.predicted_value}` : '';
            childName += ` : ${bd.predicted_source_n2} ${op} ${bd.predicted_source_n1}${rhs}`;
          }
        }
        rows.push({
          sortNum: parentNum,
          sortSub: b,
          key: `${pk}_b${b}`,
          name: childName,
          description: `Same-ball ${subLabel} on ball position ${b} only.`,
          // Route this per-ball triplet event straight into the matching
          // Ball K group (e.g. `ball_1`) via the derived group_target below.
          group_target: `ball_${b}`,
          avg_bounce_back: bd.avg_bounce_back,
          current_gap: bd.current_gap,
          min_gap: bd.min_gap,
          min_gap_last_date: bd.min_gap_last_date,
          min_gap_last_draw_number: bd.min_gap_last_draw_number,
          last_event_date: bd.last_event_date,
          last_event_draw_number: bd.last_event_draw_number,
          triplet_ball: b,
          triplet_predicted_value: bd.predicted_value,
          triplet_target_field: `ball_${b}`,
          avg_per_year_till_2025: bd.avg_per_year_till_2025,
          count_till_2025: bd.count_till_2025,
          count_2026: bd.count_2026,
        });
      }
    } else {
      // Compose the actual projection formula for the simple (non-triplet)
      // pose so the summary row reads e.g. `LS1 → LS2 : 9 → 9`, or
      // `LS1+LS2 → Ball 1 : 5 + 8 = 13`, or (cross-draw) `B1 − PB1 → LS1 : 5 − 2 = 3`.
      // Sources come from the latest draw (and, for `formula_cross`, from the
      // previous draw as well); targets are the predicted next-draw values.
      //
      // "Not possible" tag: when the computed predicted value falls outside
      // the target field's valid range (LS1: 1-11, LS2: 2-12, Ball_*: 1-50),
      // append " · Not possible" so the user sees at a glance that the pose
      // can't fire on the next draw even though the formula is defined.
      const inRange = poseInRange;
      let simpleName = p.name;
      let notPossible = false;
      if (p.prediction) {
        const pr = p.prediction;
        if ((pr.type === 'pairs' || pr.type === 'pairs_skip1') && Array.isArray(pr.targets) && pr.targets.length > 0) {
          const parts = pr.targets
            .filter(t => t.source_value != null)
            .map(t => `${t.source_value} → ${t.value != null ? t.value : '?'}`);
          if (parts.length > 0) simpleName += ` : ${parts.join(', ')}`;
          // Combination poses require ALL target positions to match together —
          // if EVEN ONE of the projected values falls outside its target
          // field's valid range, the whole combination cannot fire.
          notPossible = pr.targets.length > 0 && pr.targets.some(t => !inRange(t.field, t.value));
        } else if (pr.type === 'formula' || pr.type === 'formula_cross') {
          const inputs = pr.formula_inputs || [];
          const op = pr.formula_operator;
          const predVal = pr.targets && pr.targets[0] ? pr.targets[0].value : null;
          const predField = pr.targets && pr.targets[0] ? pr.targets[0].field : null;
          if (inputs.length === 2 && op && op !== '×2' && op !== '÷2') {
            simpleName += ` : ${inputs[0].value ?? '?'} ${op} ${inputs[1].value ?? '?'}${predVal != null ? ` = ${predVal}` : ''}`;
          } else if (inputs.length === 1 && op) {
            simpleName += ` : ${inputs[0].value ?? '?'} ${op}${predVal != null ? ` = ${predVal}` : ''}`;
          }
          notPossible = predField != null && !inRange(predField, predVal);
        } else if (pr.type === 'intra_parity') {
          // "LS1 & LS2 both even (same draw) : LS1=5, LS2=8 (mixed ✗)"
          const [av, bv] = pr.latest_values || [];
          const [al, bl] = pr.labels || [];
          const mark = pr.match ? '✓' : '✗';
          const state = pr.match
            ? `both ${pr.parity_type}`
            : 'mixed';
          simpleName += ` : ${al}=${av ?? '?'}, ${bl}=${bv ?? '?'} (${state} ${mark})`;
        } else if (pr.type === 'intra_boundary') {
          // "B1 = 1 (same draw) : latest B1=5 (target=1) ✗"
          const mark = pr.match ? '✓' : '✗';
          simpleName += ` : latest ${pr.label}=${pr.latest_value ?? '?'} (target ${pr.target_value}) ${mark}`;
        } else if (pr.type === 'recur_any_ball') {
          // "Recent B1 → appears anywhere in next : looking for 5"
          simpleName += ` : looking for ${pr.source_value ?? '?'}`;
        } else if (pr.type === 'recur_any_combo') {
          // "PB1 & PB2 → both reappear anywhere in next : looking for 2 & 12"
          const parts = (pr.source_values || [])
            .map(s => `${s.label}=${s.value ?? '?'}`);
          if (parts.length > 0) simpleName += ` : looking for ${parts.join(' & ')}`;
        } else if (pr.type === 'intra_twin_prime') {
          // "B1 & B2 = twin primes (same draw) : latest B1=3, B2=5 (twin ✓)"
          const [av, bv] = pr.latest_values || [];
          const [al, bl] = pr.labels || [];
          const mark = pr.match ? '✓' : '✗';
          const state = pr.match ? 'twin' : 'not twin';
          simpleName += ` : latest ${al}=${av ?? '?'}, ${bl}=${bv ?? '?'} (${state} ${mark})`;
        } else if (pr.type === 'intra_in_set') {
          // "B1 = perfect square (same draw) : latest B1=5 (perfect square ✗)"
          const mark = pr.match ? '✓' : '✗';
          simpleName += ` : latest ${pr.label}=${pr.latest_value ?? '?'} (${pr.family_label} ${mark})`;
        } else if (pr.type === 'intra_all_in_range') {
          // "B1..B3 all in [1, 10] (same draw) : latest B1=5, B2=29, B3=33 (out ✗)"
          const mark = pr.match ? '✓' : '✗';
          const parts = (pr.latest_values || [])
            .map(lv => `${lv.label}=${lv.value ?? '?'}`)
            .join(', ');
          const state = pr.match ? 'all inside' : 'out';
          simpleName += ` : latest ${parts} (${state} ${mark})`;
        } else if (pr.type === 'date_component') {
          // "Draw day = 24 · latest draw was day 7 ✗"
          const mark = pr.match ? '✓' : '✗';
          const _compLabel = { day: 'day', month: 'month', year: 'year' }[pr.component] || pr.component;
          simpleName += ` · latest draw ${_compLabel} = ${pr.latest_value ?? '?'} ${mark}`;
        }
      }
      if (notPossible) simpleName += '  ·  Not possible';
      // Derive group_target for ball-targeting poses that DON'T have an
      // explicit backend tag. If the pose's prediction lands on a single
      // `ball_K` field, route the row into the Ball K group. Multi-target
      // poses (e.g. LS1+LS2 → Ball 1 + Ball 2) and cross-ball poses like
      // Diagonal Pickup keep their backend tag (or fall through to
      // "Other"). LS-targeting poses already carry `group_target` from the
      // backend, so this only affects previously-ungrouped ball poses.
      let derivedGroup = p.group_target;
      if (!derivedGroup && p.prediction && Array.isArray(p.prediction.targets) && p.prediction.targets.length === 1) {
        const tgtField = p.prediction.targets[0].field;
        if (tgtField && /^ball_[1-5]$/.test(tgtField)) {
          derivedGroup = tgtField;
        }
      }
      rows.push({
        sortNum: parentNum,
        sortSub: 0,
        sno: parentLabel,
        poseNum: parentLabel,
        key: pk,
        name: simpleName,
        notPossible,
        description: p.description,
        // Attach the valid-values / valid-pairs list for set-membership +
        // twin-prime poses so the row can render a "Valid: 1, 3, 6, …" chip
        // under the description without having to re-derive the family here.
        familyValues: (p.prediction && p.prediction.type === 'intra_in_set')
          ? { label: p.prediction.family_label, values: p.prediction.family_values }
          : (p.prediction && p.prediction.type === 'intra_twin_prime')
            ? { label: 'twin-prime pair', values: ['(3, 5)', '(5, 7)', '(11, 13)', '(17, 19)', '(29, 31)', '(41, 43)'] }
            : null,
        group_target: derivedGroup,
        group_order: p.group_order,
        avg_bounce_back: p.avg_bounce_back,
        current_gap: p.current_gap,
        min_gap: p.min_gap,
        min_gap_last_date: p.min_gap_last_date,
        min_gap_last_draw_number: p.min_gap_last_draw_number,
        last_event_date: p.last_event_date,
        last_event_draw_number: p.last_event_draw_number,
        prediction: p.prediction,
        avg_per_year_till_2025: p.avg_per_year_till_2025,
        count_till_2025: p.count_till_2025,
        count_2026: p.count_2026,
      });
    }
  });

  // ------------------------------------------------------------------
  // Group + renumber. Groups are keyed by `group_target`:
  //   • lucky_star_1 / lucky_star_2                       → Groups 1 / 2
  //   • lucky_star_combination                            → Group 3
  //   • ball_1 / ball_2 / ball_3 / ball_4 / ball_5        → Groups 4 / 5 / 6 / 7 / 8
  //   • anything else (Diagonal Pickup, LS1+LS2 → Ball1+Ball2, LS×N)
  //                                                       → Group 9 (Other)
  // Sub-numbering restarts inside each group: 1.1 … · 4.1 …
  // ------------------------------------------------------------------
  const GROUP_META = [
    { id: 'lucky_star_1',           label: '1. LS1 Related poses',        subtitle: 'These poses project the value of LS1 in the next draw.',                                index: 1, tone: 'lucky_star_1' },
    { id: 'lucky_star_2',           label: '2. LS2 Related poses',        subtitle: 'These poses project the value of LS2 in the next draw.',                                index: 2, tone: 'lucky_star_2' },
    { id: 'lucky_star_combination', label: '3. LS1 + LS2 Combination',    subtitle: 'These poses fire only when BOTH LS1 AND LS2 of the next draw match the same source pair.', index: 3, tone: 'lucky_star_combination' },
    { id: 'ball_1',                 label: '4. Ball 1 Related poses',     subtitle: 'These poses project the value of Ball 1 in the next draw.',                              index: 4, tone: 'ball_1' },
    { id: 'ball_2',                 label: '5. Ball 2 Related poses',     subtitle: 'These poses project the value of Ball 2 in the next draw.',                              index: 5, tone: 'ball_2' },
    { id: 'ball_3',                 label: '6. Ball 3 Related poses',     subtitle: 'These poses project the value of Ball 3 in the next draw.',                              index: 6, tone: 'ball_3' },
    { id: 'ball_4',                 label: '7. Ball 4 Related poses',     subtitle: 'These poses project the value of Ball 4 in the next draw.',                              index: 7, tone: 'ball_4' },
    { id: 'ball_5',                 label: '8. Ball 5 Related poses',     subtitle: 'These poses project the value of Ball 5 in the next draw.',                              index: 8, tone: 'ball_5' },
    { id: 'other',                  label: '9. Other poses',              subtitle: 'Cross-ball or multi-target poses (Diagonal Pickup, LS→Ball combinations, LS×N).',           index: 9, tone: 'other' },
  ];
  const GROUP_IDS = new Set(GROUP_META.map((g) => g.id));
  const grouped = { lucky_star_1: [], lucky_star_2: [], lucky_star_combination: [], ball_1: [], ball_2: [], ball_3: [], ball_4: [], ball_5: [], other: [] };
  rows.forEach((r) => {
    const bucket = GROUP_IDS.has(r.group_target) && r.group_target !== 'other' ? r.group_target : 'other';
    grouped[bucket].push(r);
  });
  // Sort inside each group by historical **Avg / year (till 2025)** DESC —
  // the pose that fires most often per calendar year sits at the top.
  // Tie-break: fall back to the manually-assigned group_order so identical-
  // frequency rows keep a stable, deterministic position.
  const sortByAvgYearDesc = (a, b) => {
    const av = a.avg_per_year_till_2025;
    const bv = b.avg_per_year_till_2025;
    const aNull = av == null;
    const bNull = bv == null;
    if (aNull && bNull) return (a.group_order || 999) - (b.group_order || 999);
    if (aNull) return 1;
    if (bNull) return -1;
    if (bv !== av) return bv - av;
    return (a.group_order || 999) - (b.group_order || 999);
  };
  grouped.lucky_star_1.sort(sortByAvgYearDesc);
  grouped.lucky_star_2.sort(sortByAvgYearDesc);
  grouped.lucky_star_combination.sort(sortByAvgYearDesc);
  grouped.ball_1.sort(sortByAvgYearDesc);
  grouped.ball_2.sort(sortByAvgYearDesc);
  grouped.ball_3.sort(sortByAvgYearDesc);
  grouped.ball_4.sort(sortByAvgYearDesc);
  grouped.ball_5.sort(sortByAvgYearDesc);
  grouped.other.sort((a, b) => (a.sortNum - b.sortNum) || (a.sortSub - b.sortSub));

  // Flatten and renumber with new sno / poseNum values. Triplet children
  // are now first-class rows within their Ball K group, so a single flat
  // sub-index applies per group (e.g. `4.1`, `4.2`, … · `5.1` … · `9.1` …).
  const flat = [];
  GROUP_META.forEach((g) => {
    const bucket = grouped[g.id];
    if (bucket.length === 0) return;
    flat.push({ isGroupHeader: true, groupId: g.id, groupLabel: g.label, groupSubtitle: g.subtitle, tone: g.tone, key: `__grp_${g.id}` });
    let subIdx = 0;
    bucket.forEach((r) => {
      subIdx += 1;
      r.sno     = `${g.index}.${subIdx}`;
      r.poseNum = r.sno;
      flat.push(r);
    });
  });

  // Replace `rows` with the flattened, renumbered, group-aware list so all
  // downstream calculations (targetGroups / render) walk the same order.
  rows.length = 0;
  Array.prototype.push.apply(rows, flat);

  // Group actionable rows (DUE / OVERDUE / DEEP OVERDUE / APPROACHING) by the
  // ball / lucky-star position they predict — used by the top Prediction Card.
  const targetGroups = { ball_1: [], ball_2: [], ball_3: [], ball_4: [], ball_5: [], lucky_star_1: [], lucky_star_2: [] };
  rows.forEach((r) => {
    if (r.isParent) return; // Parents aggregate their children; skip
    if (r.avg_bounce_back == null) return;
    const g = r.current_gap;
    const av = r.avg_bounce_back;
    let tier;
    if (g >= 2 * av) tier = 'overdue';
    else if (g >= av) tier = 'due';
    else if (g >= av * 0.75) tier = 'approaching';
    else return;
    // Collect targets for this pose
    let targets = [];
    if (r.triplet_predicted_value != null) {
      targets = [{ field: r.triplet_target_field, value: r.triplet_predicted_value }];
    } else if (r.prediction && Array.isArray(r.prediction.targets)) {
      targets = r.prediction.targets;
    }
    targets.forEach((t) => {
      if (!targetGroups[t.field]) return;
      // Use the structural per-field range (LS1: 1-11, LS2: 2-12, ball_*:
      // 1-50) — LS1 CANNOT be 12 and LS2 CANNOT be 1, so combo poses that
      // project such values must be flagged invalid.
      const isValidBall = poseInRange(t.field, t.value);
      targetGroups[t.field].push({
        pose: r.sno,
        name: r.name,
        formula_label: (r.prediction && r.prediction.formula_label) || null,
        value: t.value,
        isValid: isValidBall,
        tier,
        current_gap: g,
        avg_bounce_back: av,
      });
    });
  });
  // Sort each group by tier severity, then higher gap first
  const tierRank = { overdue: 3, due: 2, approaching: 1, fresh: 0 };

  // Every displayed W3 Never candidate participates, with no extra timing gate.
  ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'].forEach(field => {
    const { candidates } = readNeverCandidates(neverInMonthPoses, field);
    candidates.forEach(rp => targetGroups[field].push({
      pose: 'R', name: `W3 Never in ${rp.current_month_name}`,
      formula_label: `${rp.last_value} ${rp.diff >= 0 ? '+' : ''}${rp.diff}`,
      value: rp.predicted_number, diff: rp.diff, isValid: true,
      tier: (rp.tier || 'FRESH').toLowerCase(), current_gap: rp.current_gap,
      avg_bounce_back: rp.avg_bounce_back, isNeverInMonth: true,
    }));
  });

  Object.keys(targetGroups).forEach((k) => {
    targetGroups[k].sort((a, b) => (tierRank[b.tier] - tierRank[a.tier]) || (b.current_gap - a.current_gap));
  });

  // Overdue Summary — flat list of every pose whose current gap has crossed
  // 2× its average bounce-back (tier === 'overdue'). Primary sort by
  // annual firing rate (avg_per_year_till_2025 DESC) so the "loudest"
  // poses lead; secondary sort by overdue ratio (current_gap / avg_bb DESC)
  // so within the same frequency band the most-overdue rises first.
  // Excludes triplet parent rows (children carry the real numbers) and
  // insufficient-sample poses (avg_bounce_back missing).
  const overdueSummary = rows
    .filter((r) => !r.isParent && r.avg_bounce_back != null)
    .map((r) => ({
      row: r,
      ratio: r.current_gap / r.avg_bounce_back,
    }))
    .filter((x) => x.ratio >= 2)
    .sort((a, b) => {
      const ayr = a.row.avg_per_year_till_2025 || 0;
      const byr = b.row.avg_per_year_till_2025 || 0;
      if (byr !== ayr) return byr - ayr;
      return b.ratio - a.ratio;
    });

  // Due Summary — mirrors overdueSummary but for the "due" tier: current
  // gap has crossed the average bounce-back (ratio ≥ 1) but NOT yet 2×
  // (which would make it overdue). Same primary/secondary sort as
  // overdueSummary so behaviour is consistent.
  const dueSummary = rows
    .filter((r) => !r.isParent && r.avg_bounce_back != null)
    .map((r) => ({
      row: r,
      ratio: r.current_gap / r.avg_bounce_back,
    }))
    .filter((x) => x.ratio >= 1 && x.ratio < 2)
    .sort((a, b) => {
      const ayr = a.row.avg_per_year_till_2025 || 0;
      const byr = b.row.avg_per_year_till_2025 || 0;
      if (byr !== ayr) return byr - ayr;
      return b.ratio - a.ratio;
    });

  // Hot-in-2026 Summary — poses whose 2026 pace already exceeds their
  // historical annual rate. year_progress = fraction of 2026 elapsed
  // (based on today's date, capped at 1). Expected 2026 count =
  // avg_per_year_till_2025 × year_progress. Performance ratio =
  // count_2026 / expected. Only include poses where the ratio > 1 and
  // count_2026 ≥ 1 so under-firing / never-fired poses are hidden.
  const _now = new Date();
  const _startOfYear = new Date(_now.getFullYear(), 0, 1);
  const _yearProgress = Math.min(1, Math.max(0.01,
    (_now - _startOfYear) / (365 * 24 * 60 * 60 * 1000)
  ));

  // Renders a small "% achievement" chip that compares the pose's 2026 count
  // to its historical annual rate, adjusted for how much of 2026 has already
  // elapsed. Green ≥ 100%, amber 50–99%, slate < 50%. Returns null when the
  // ratio can't be computed (no historical rate).
  const paceChip = (count2026, avgPerYear) => {
    const c = count2026 || 0;
    const rate = avgPerYear || 0;
    if (rate <= 0) return null;
    const expected = rate * _yearProgress;
    const pct = expected > 0 ? Math.round((c / expected) * 100) : 0;
    const tone = pct >= 100
      ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
      : pct >= 50
        ? 'bg-amber-100 text-amber-800 border-amber-300'
        : 'bg-slate-100 text-slate-600 border-slate-300';
    return (
      <span
        className={`ml-1 inline-flex items-center rounded-md border px-1 py-0 font-mono font-bold text-[9px] ${tone}`}
        title={`2026 pace: ${c} actual vs ${expected.toFixed(2)} expected (${(_yearProgress * 100).toFixed(0)}% of year elapsed)`}
      >
        {pct}%
      </span>
    );
  };
  const hotSummary = rows
    .filter((r) => !r.isParent && (r.avg_per_year_till_2025 || 0) > 0 && (r.count_2026 || 0) >= 1)
    .map((r) => {
      const expected = (r.avg_per_year_till_2025 || 0) * _yearProgress;
      return {
        row: r,
        expected,
        ratio: expected > 0 ? (r.count_2026 || 0) / expected : 0,
      };
    })
    .filter((x) => x.ratio > 1)
    .sort((a, b) => {
      // Primary sort: annual firing rate DESC (loudest first) so
      // high-signal frequent poses lead — matches the Overdue/Due
      // summary sort convention. Secondary tiebreak by pace ratio DESC.
      const ayr = a.row.avg_per_year_till_2025 || 0;
      const byr = b.row.avg_per_year_till_2025 || 0;
      if (byr !== ayr) return byr - ayr;
      return b.ratio - a.ratio;
    });

  const anyActionable = Object.values(targetGroups).some((arr) => arr.length > 0);

  if (rows.length === 0) return null;

  const BALL_ORDER = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const LS_ORDER = ['lucky_star_1', 'lucky_star_2'];

  return (
    <div className="space-y-4 w37-mobile-summary" ref={mobileTablesRef}>
      {/* Overdue Summary — flat, focused list of every OVERDUE pose sorted
          by annual firing frequency first (loudest signals lead) then by
          overdue ratio (most-overdue within a frequency band). Distinct
          from the Prediction Card below because it doesn't group by ball
          position — it's a one-glance "which poses are shouting the
          loudest right now" panel. */}
      {overdueSummary.length > 0 && (
        <div className="rounded-2xl border-2 border-rose-500 bg-white shadow-lg overflow-hidden" data-testid="w37-overdue-summary">
          <div className="px-5 py-3 bg-gradient-to-r from-rose-700 via-rose-600 to-orange-600 text-white flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider">🔥 Overdue Summary</h3>
              <p className="text-[11px] text-white/85 italic">Every pose whose current gap ≥ 2× avg bounce-back. Sorted by annual firing rate (loudest first) then overdue ratio.</p>
            </div>
            <span className="text-[10px] font-mono bg-white/20 rounded px-2 py-1" data-testid="w37-overdue-summary-count">
              {overdueSummary.length} overdue pose{overdueSummary.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="w37-overdue-summary-table">
              <thead className="bg-rose-50/70 border-b-2 border-rose-200">
                <tr>
                  <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-14">#</th>
                  <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-14">Pose</th>
                  <th className="px-3 py-2 text-left uppercase text-rose-800 font-black">Name</th>
                  <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-24">Times / year</th>
                  <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-14">2026</th>
                  <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-20">Current gap</th>
                  <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-20">Avg bounce-back</th>
                  <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-20">Overdue</th>
                </tr>
              </thead>
              <tbody>
                {overdueSummary.map((x, i) => {
                  const r = x.row;
                  const ratio = x.ratio;
                  const pct = Math.round((ratio - 1) * 100); // "how much past average" as %
                  // Deep-overdue accent — ratio ≥ 4× gets a stronger tint.
                  const rowCls = ratio >= 4
                    ? 'bg-rose-100/70'
                    : ratio >= 3
                      ? 'bg-rose-50/60'
                      : (i % 2 === 0 ? 'bg-white' : 'bg-rose-50/30');
                  return (
                    <tr key={r.key} className={`border-b border-rose-100 ${rowCls}`} data-testid={`w37-overdue-summary-row-${i}`}>
                      <td className="px-2 py-2 text-center font-mono text-slate-400 text-[11px]">{i + 1}</td>
                      <td className="px-2 py-2 text-center font-mono font-black text-rose-700">{r.sno}</td>
                      <td className="px-3 py-2 text-slate-800">
                        <div className="font-semibold text-[12px] leading-tight">{r.name}</div>
                        {r.notPossible && (
                          <div className="text-[9px] uppercase font-black tracking-wider text-amber-700">Not possible</div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className="inline-flex items-center justify-center rounded-md bg-slate-100 border border-slate-300 px-2 py-0.5 font-mono font-bold text-slate-700">
                          {(r.avg_per_year_till_2025 || 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center font-mono text-slate-700">
                        {r.count_2026 ?? '—'}
                        {paceChip(r.count_2026, r.avg_per_year_till_2025)}
                      </td>
                      <td className="px-2 py-2 text-center font-mono font-bold text-slate-800">{r.current_gap}</td>
                      <td className="px-2 py-2 text-center font-mono text-slate-600">{r.avg_bounce_back?.toFixed(2)}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono font-black text-[11px] ${ratio >= 4 ? 'bg-rose-700 text-white ring-2 ring-amber-400' : ratio >= 3 ? 'bg-rose-600 text-white' : 'bg-rose-500 text-white'}`}>
                          ×{ratio.toFixed(1)}
                          <span className="text-[9px] opacity-80">(+{pct}%)</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-slate-50/70 border-t border-rose-100 text-[10px] text-slate-600 italic">
            <b>Overdue ×N</b> = current gap / avg bounce-back. Anything ≥ 2.0 is overdue; ≥ 3.0 is a strong signal; ≥ 4.0 (ringed) is a deep-overdue anomaly. Ranking prioritises poses that fire often per year so the top of the list = high-signal + past-due.
          </div>
        </div>
      )}

      {/* Due Summary — mirror of Overdue Summary but for the "due" tier
          (ratio ≥ 1, ratio < 2). Amber/orange colour scheme signals a
          moderate-urgency band: the pose has crossed its average bounce-
          back but hasn't yet blown past 2× (which would elevate it to
          overdue). Same sort as Overdue Summary. */}
      {dueSummary.length > 0 && (
        <div className="rounded-2xl border-2 border-amber-500 bg-white shadow-lg overflow-hidden" data-testid="w37-due-summary">
          <div className="px-5 py-3 bg-gradient-to-r from-amber-600 via-orange-500 to-orange-600 text-white flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider">⚠️ Due Summary</h3>
              <p className="text-[11px] text-white/85 italic">Every pose whose current gap sits between 1× and 2× its avg bounce-back. Sorted by annual firing rate (loudest first) then due ratio.</p>
            </div>
            <span className="text-[10px] font-mono bg-white/20 rounded px-2 py-1" data-testid="w37-due-summary-count">
              {dueSummary.length} due pose{dueSummary.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="w37-due-summary-table">
              <thead className="bg-amber-50/70 border-b-2 border-amber-200">
                <tr>
                  <th className="px-2 py-2 text-center uppercase text-amber-800 font-black w-14">#</th>
                  <th className="px-2 py-2 text-center uppercase text-amber-800 font-black w-14">Pose</th>
                  <th className="px-3 py-2 text-left uppercase text-amber-800 font-black">Name</th>
                  <th className="px-2 py-2 text-center uppercase text-amber-800 font-black w-24">Times / year</th>
                  <th className="px-2 py-2 text-center uppercase text-amber-800 font-black w-14">2026</th>
                  <th className="px-2 py-2 text-center uppercase text-amber-800 font-black w-20">Current gap</th>
                  <th className="px-2 py-2 text-center uppercase text-amber-800 font-black w-20">Avg bounce-back</th>
                  <th className="px-2 py-2 text-center uppercase text-amber-800 font-black w-20">Due</th>
                </tr>
              </thead>
              <tbody>
                {dueSummary.map((x, i) => {
                  const r = x.row;
                  const ratio = x.ratio;
                  const pct = Math.round((ratio - 1) * 100);
                  const rowCls = ratio >= 1.5
                    ? 'bg-amber-100/60'
                    : (i % 2 === 0 ? 'bg-white' : 'bg-amber-50/30');
                  return (
                    <tr key={r.key} className={`border-b border-amber-100 ${rowCls}`} data-testid={`w37-due-summary-row-${i}`}>
                      <td className="px-2 py-2 text-center font-mono text-slate-400 text-[11px]">{i + 1}</td>
                      <td className="px-2 py-2 text-center font-mono font-black text-amber-700">{r.sno}</td>
                      <td className="px-3 py-2 text-slate-800">
                        <div className="font-semibold text-[12px] leading-tight">{r.name}</div>
                        {r.notPossible && (
                          <div className="text-[9px] uppercase font-black tracking-wider text-amber-700">Not possible</div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className="inline-flex items-center justify-center rounded-md bg-slate-100 border border-slate-300 px-2 py-0.5 font-mono font-bold text-slate-700">
                          {(r.avg_per_year_till_2025 || 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center font-mono text-slate-700">
                        {r.count_2026 ?? '—'}
                        {paceChip(r.count_2026, r.avg_per_year_till_2025)}
                      </td>
                      <td className="px-2 py-2 text-center font-mono font-bold text-slate-800">{r.current_gap}</td>
                      <td className="px-2 py-2 text-center font-mono text-slate-600">{r.avg_bounce_back?.toFixed(2)}</td>
                      <td className="px-2 py-2 text-center">
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono font-black text-[11px] bg-amber-500 text-white">
                          ×{ratio.toFixed(2)}
                          <span className="text-[9px] opacity-80">(+{pct}%)</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-slate-50/70 border-t border-amber-100 text-[10px] text-slate-600 italic">
            <b>Due ×N</b> = current gap / avg bounce-back, in the range [1.0, 2.0). Above 2.0 the pose escalates to the Overdue Summary above. Top of the list = high annual signal + already past average.
          </div>
        </div>
      )}

      {/* Hot-in-2026 Summary — poses whose 2026 pace is running ABOVE
          their historical annual rate. `year_progress` is the fraction of
          2026 elapsed based on today's date, and `expected` = historical
          rate × year_progress. `ratio` = actual / expected — anything > 1
          means the pose is firing hotter than usual for this point in the
          year. Emerald/green scheme signals a positive momentum. */}
      {hotSummary.length > 0 && (
        <div className="rounded-2xl border-2 border-emerald-500 bg-white shadow-lg overflow-hidden" data-testid="w37-hot-summary">
          <div className="px-5 py-3 bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 text-white flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider">🔥 Hot in 2026 Summary</h3>
              <p className="text-[11px] text-white/85 italic">Poses whose 2026 pace exceeds their historical annual rate ({(_yearProgress * 100).toFixed(0)}% of 2026 elapsed). Sorted by how much they&apos;re outperforming.</p>
            </div>
            <span className="text-[10px] font-mono bg-white/20 rounded px-2 py-1" data-testid="w37-hot-summary-count">
              {hotSummary.length} hot pose{hotSummary.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="w37-hot-summary-table">
              <thead className="bg-emerald-50/70 border-b-2 border-emerald-200">
                <tr>
                  <th className="px-2 py-2 text-center uppercase text-emerald-800 font-black w-14">#</th>
                  <th className="px-2 py-2 text-center uppercase text-emerald-800 font-black w-14">Pose</th>
                  <th className="px-3 py-2 text-left uppercase text-emerald-800 font-black">Name</th>
                  <th className="px-2 py-2 text-center uppercase text-emerald-800 font-black w-24">Times / year</th>
                  <th className="px-2 py-2 text-center uppercase text-emerald-800 font-black w-14">2026</th>
                  <th className="px-2 py-2 text-center uppercase text-emerald-800 font-black w-24">Expected&nbsp;2026</th>
                  <th className="px-2 py-2 text-center uppercase text-emerald-800 font-black w-20">Pace vs avg</th>
                </tr>
              </thead>
              <tbody>
                {hotSummary.map((x, i) => {
                  const r = x.row;
                  const ratio = x.ratio;
                  const pct = Math.round((ratio - 1) * 100);
                  // Strong-hot accent for ≥ 2× expected (i.e. doubled the annual pace).
                  const rowCls = ratio >= 2.5
                    ? 'bg-emerald-100/70'
                    : ratio >= 1.75
                      ? 'bg-emerald-50/70'
                      : (i % 2 === 0 ? 'bg-white' : 'bg-emerald-50/30');
                  return (
                    <tr key={r.key} className={`border-b border-emerald-100 ${rowCls}`} data-testid={`w37-hot-summary-row-${i}`}>
                      <td className="px-2 py-2 text-center font-mono text-slate-400 text-[11px]">{i + 1}</td>
                      <td className="px-2 py-2 text-center font-mono font-black text-emerald-700">{r.sno}</td>
                      <td className="px-3 py-2 text-slate-800">
                        <div className="font-semibold text-[12px] leading-tight">{r.name}</div>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className="inline-flex items-center justify-center rounded-md bg-slate-100 border border-slate-300 px-2 py-0.5 font-mono font-bold text-slate-700">
                          {(r.avg_per_year_till_2025 || 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center font-mono font-bold text-emerald-700">
                        {r.count_2026 ?? 0}
                        {paceChip(r.count_2026, r.avg_per_year_till_2025)}
                      </td>
                      <td className="px-2 py-2 text-center font-mono text-slate-500">{x.expected.toFixed(2)}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono font-black text-[11px] ${ratio >= 2.5 ? 'bg-emerald-700 text-white ring-2 ring-amber-400' : ratio >= 1.75 ? 'bg-emerald-600 text-white' : 'bg-emerald-500 text-white'}`}>
                          ×{ratio.toFixed(2)}
                          <span className="text-[9px] opacity-80">(+{pct}%)</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-slate-50/70 border-t border-emerald-100 text-[10px] text-slate-600 italic">
            <b>Pace vs avg</b> = actual 2026 count ÷ (avg/year × fraction of 2026 elapsed). Anything &gt; ×1.0 means the pose is outperforming its historical pace. Ranking prioritises the strongest over-performers — top of the list = pose most exceeding its usual rhythm.
          </div>
        </div>
      )}

      {/* Prediction Card — synthesised from DUE / OVERDUE / APPROACHING poses */}
      <div className="rounded-2xl border-2 border-rose-400 bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 shadow-md overflow-hidden" data-testid="w37-prediction-card">
        <div className="px-5 py-3 bg-gradient-to-r from-rose-600 via-orange-600 to-amber-500 text-white flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-black uppercase tracking-wider">🎯 Prediction for the Next Draw</h3>
            <p className="text-[11px] text-white/85 italic" data-testid="w37-prediction-sources">DUE / OVERDUE / APPROACHING Yoga poses + all W3 Never-in-{neverMonthName(neverInMonthMeta)} candidates, regardless of timing status.</p>
          </div>
          <span className="text-[10px] font-mono bg-white/20 rounded px-2 py-1">
            source draw = {rows[0]?.prediction?.source_draw_number || '—'} · {rows[0]?.prediction?.source_date || ''}
          </span>
        </div>
        <div className="p-4 space-y-4">
          {!anyActionable ? (
            <p className="text-sm text-gray-600 italic text-center py-4">
              No eligible Yoga poses or W3 Never candidates for the next draw.
            </p>
          ) : (
            <>
              {/* Ball predictions */}
              <div>
                <h4 className="text-[11px] font-black uppercase tracking-wider text-gray-700 mb-2">Main balls</h4>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  {BALL_ORDER.map((bf, i) => {
                    const arr = targetGroups[bf] || [];
                    const validArr = arr.filter(x => x.isValid);
                    const isMid = i >= 1 && i <= 3;
                    return (
                      <div key={bf} className={`rounded-lg p-3 border ${isMid ? 'bg-white border-amber-300 shadow' : 'bg-white/80 border-gray-200'}`} data-testid={`w37-pred-group-${bf}`}>
                        <div className={`text-xs font-black uppercase tracking-wider mb-2 ${isMid ? 'text-amber-900' : 'text-gray-700'}`}>
                          Ball {i + 1}{isMid ? ' ⚠' : ''}
                          <span className="text-[10px] text-gray-400 font-mono ml-1">({validArr.length}/{arr.length} valid)</span>
                        </div>
                        {arr.length === 0 ? (
                          <p className="text-[11px] text-gray-400 italic">No pose active on this ball.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {arr.map((p, pi) => {
                              const tierCls = p.tier === 'overdue' ? 'bg-rose-700 text-white ring-2 ring-amber-400'
                                : p.tier === 'due' ? 'bg-rose-500 text-white'
                                : p.tier === 'fresh' ? 'bg-cyan-100 text-cyan-950 ring-1 ring-cyan-400' : 'bg-amber-400 text-amber-900';
                              return (
                                <div key={`${p.pose}-${pi}`} className={`rounded px-2 py-1 flex items-center gap-2 ${!p.isValid ? 'opacity-50' : ''}`} data-testid={p.isNeverInMonth ? `w37-prediction-never-${bf}-${p.value}` : `w37-prediction-${bf}-${p.pose}-${pi}`} data-w37-export-card-row="Next Draw Predictions" data-w37-export-context={`Ball ${i + 1}`}>
                                  <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-black text-sm ${p.isValid ? tierCls : 'bg-gray-300 text-gray-500'}`}>
                                    {p.value == null || !p.isValid ? '—' : p.value}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[11px] font-bold text-gray-800 truncate" title={p.name}>
                                      {p.isNeverInMonth ? 'Pose R · W3 Never' : `Pose ${p.pose}`}
                                    </div>
                                    <div className="text-[9px] text-gray-500 truncate uppercase font-bold">
                                      {p.tier} · gap {p.current_gap}/{p.avg_bounce_back}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* LS predictions */}
              {(targetGroups.lucky_star_1.length > 0 || targetGroups.lucky_star_2.length > 0) && (
                <div>
                  <h4 className="text-[11px] font-black uppercase tracking-wider text-gray-700 mb-2">Lucky Stars</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {LS_ORDER.map((lf, i) => {
                      const arr = targetGroups[lf] || [];
                      return (
                        <div key={lf} className="rounded-lg p-3 border bg-yellow-50 border-amber-300" data-testid={`w37-pred-group-${lf}`}>
                          <div className="text-xs font-black uppercase tracking-wider mb-2 text-amber-900">
                            LS{i + 1}
                            <span className="text-[10px] text-gray-500 font-mono ml-1">({arr.length})</span>
                          </div>
                          {arr.length === 0 ? (
                            <p className="text-[11px] text-gray-400 italic">No pose active on this LS.</p>
                          ) : (
                            <div className="space-y-1.5">
                              {arr.map((p, pi) => {
                                const tierCls = p.tier === 'overdue' ? 'bg-rose-700 text-white ring-2 ring-amber-400'
                                  : p.tier === 'due' ? 'bg-rose-500 text-white'
                                  : 'bg-amber-400 text-amber-900';
                                return (
                                  <div key={`${p.pose}-${pi}`} className={`rounded px-2 py-1 flex items-center gap-2 ${!p.isValid ? 'opacity-50' : ''}`} data-testid={`w37-prediction-${lf}-${p.pose}-${pi}`} data-w37-export-card-row="Next Draw Predictions" data-w37-export-context={`LS${i + 1}`}>
                                    <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-black text-sm ${p.isValid ? tierCls : 'bg-gray-300 text-gray-500'}`}>
                                      {p.value == null || !p.isValid ? '—' : p.value}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-[11px] font-bold text-gray-800 truncate" title={p.name}>
                                        Pose {p.pose}
                                      </div>
                                      <div className="text-[9px] text-gray-500 truncate uppercase font-bold">
                                        {p.tier} · gap {p.current_gap}/{p.avg_bounce_back}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="text-[10px] text-gray-500 italic bg-white/60 rounded px-2 py-1 mt-2">
                <b>Legend:</b> chip colour tracks verdict — <span className="inline-block px-1 rounded bg-amber-400 text-amber-900 font-black">approaching</span>, <span className="inline-block px-1 rounded bg-rose-500 text-white font-black">DUE</span>, <span className="inline-block px-1 rounded bg-rose-700 text-white font-black ring-2 ring-amber-400">overdue</span>. Grey/faded = predicted value is out of range (e.g. Ball sum &gt; 50) — pose is due but cannot fire from the current formula.
              </div>
            </>
          )}
        </div>
      </div>

      <W37NeverMonthPanel byBall={neverInMonthPoses} meta={neverInMonthMeta} />

      <div className="rounded-2xl border-2 border-amber-300 bg-white overflow-hidden shadow-md" data-testid="w37-poses-summary">
      <div className="px-5 py-3 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-black uppercase tracking-wider">Poses Summary — one-glance vitals</h3>
            <p className="text-[11px] text-white/85 italic">
              Every pose in a single row. Same-ball poses (9, 10, 12) are broken down per ball position (e.g. 9.1 = Ball 1). Auto-refreshes after every new draw.
            </p>
          </div>
          <span className="text-[10px] font-mono bg-white/20 rounded px-2 py-1">
            avg divisor = <strong>{nYearsTill2025 || '—'}</strong> pre-2026 years
          </span>
        </div>
      </div>

      {/* Draw-reference legend: gives the user a quick decoder for the naming
          convention used throughout the pose descriptions/headers.
          - B_K, LS_K  → recent (latest) draw
          - PB_K, PLS_K → previous draw (N-1)
          - P2B_K, P2LS_K → previous-to-previous draw (N-2) */}
      <DrawReferenceLegend recent={recentDraw} prev={previousDraw} prev2={previousPreviousDraw} />
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="w37-poses-summary-table">
          <thead className="bg-amber-50 border-b border-amber-200">
            <tr>
              <th className="px-3 py-2 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider w-16">S.No.</th>
              <th className="px-3 py-2 text-left  text-[10px] font-black text-amber-900 uppercase tracking-wider">Pose</th>
              <th className="px-3 py-2 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider" title="Average draws between consecutive events">Avg<br />Bounce-Back</th>
              <th className="px-3 py-2 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider" title="Draws since the pose last fired">Current<br />Gap</th>
              <th className="px-3 py-2 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider" title="Shortest bounce-back ever observed between two consecutive firings — the fastest recorded streak. 'Since' = last date such a fast bounce occurred (NOT the last-hit date — for that see 'last hit' under Current Gap).">Best<br />bounce-back</th>
              <th className="px-3 py-2 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider" title="Historical yearly average across the pre-2026 window">Avg / year<br />(till 2025)</th>
              <th className="px-3 py-2 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider" title="Pose-events observed in 2026 so far">Times<br />in 2026</th>
              <th className="px-3 py-2 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider" title="Number(s) this pose predicts for the NEXT draw, computed from the latest completed draw.">Prediction<br />(next draw)</th>
              <th className="px-3 py-2 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              // Group header row — a full-width divider between LS / Ball K /
              // Combination pose groups. Uses different tint per group for
              // quick scan (rose-pink for main ball groups so they stand out
              // from the amber/yellow LS families). Immediately followed by
              // a repeated column-header row so the columns stay identifiable
              // as the user scrolls into each group.
              if (r.isGroupHeader) {
                const toneCls = r.tone === 'lucky_star_1'
                  ? 'bg-gradient-to-r from-amber-100 to-amber-50 border-y-2 border-amber-300 text-amber-900'
                  : r.tone === 'lucky_star_2'
                  ? 'bg-gradient-to-r from-yellow-100 to-yellow-50 border-y-2 border-yellow-300 text-yellow-900'
                  : r.tone === 'lucky_star_combination'
                  ? 'bg-gradient-to-r from-orange-100 to-amber-100 border-y-2 border-orange-300 text-orange-900'
                  : r.tone === 'ball_1'
                  ? 'bg-gradient-to-r from-rose-100 to-rose-50 border-y-2 border-rose-300 text-rose-900'
                  : r.tone === 'ball_2'
                  ? 'bg-gradient-to-r from-pink-100 to-pink-50 border-y-2 border-pink-300 text-pink-900'
                  : r.tone === 'ball_3'
                  ? 'bg-gradient-to-r from-fuchsia-100 to-fuchsia-50 border-y-2 border-fuchsia-300 text-fuchsia-900'
                  : r.tone === 'ball_4'
                  ? 'bg-gradient-to-r from-purple-100 to-purple-50 border-y-2 border-purple-300 text-purple-900'
                  : r.tone === 'ball_5'
                  ? 'bg-gradient-to-r from-indigo-100 to-indigo-50 border-y-2 border-indigo-300 text-indigo-900'
                  : 'bg-gradient-to-r from-slate-100 to-slate-50 border-y-2 border-slate-300 text-slate-800';
                return (
                  <React.Fragment key={r.key}>
                    <tr className={toneCls} data-testid={`w37-poses-summary-group-${r.groupId}`}>
                      <td colSpan={9} className="px-4 py-2">
                        <div className="text-[12px] font-black uppercase tracking-wider">{r.groupLabel}</div>
                        {r.groupSubtitle && (
                          <div className="text-[10px] font-normal italic opacity-80 mt-0.5">{r.groupSubtitle}</div>
                        )}
                      </td>
                    </tr>
                    <tr className="bg-amber-50 border-b border-amber-200" data-testid={`w37-poses-summary-columns-${r.groupId}`}>
                      <th className="px-3 py-1.5 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider w-16">S.No.</th>
                      <th className="px-3 py-1.5 text-left  text-[10px] font-black text-amber-900 uppercase tracking-wider">Pose</th>
                      <th className="px-3 py-1.5 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider">Avg<br />Bounce-Back</th>
                      <th className="px-3 py-1.5 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider">Current<br />Gap</th>
                      <th className="px-3 py-1.5 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider">Best<br />bounce-back</th>
                      <th className="px-3 py-1.5 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider">Avg / year<br />(till 2025)</th>
                      <th className="px-3 py-1.5 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider">Times<br />in 2026</th>
                      <th className="px-3 py-1.5 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider">Prediction<br />(next draw)</th>
                      <th className="px-3 py-1.5 text-center text-[10px] font-black text-amber-900 uppercase tracking-wider">Verdict</th>
                    </tr>
                  </React.Fragment>
                );
              }
              const avg = r.avg_bounce_back;
              const gap = r.current_gap;
              let verdict, verdictCls;
              if (avg == null) {
                verdict = 'insufficient';
                verdictCls = 'bg-gray-200 text-gray-700';
              } else if (gap >= 2 * avg) {
                verdict = `overdue (×${(gap / avg).toFixed(1)})`;
                verdictCls = 'bg-rose-700 text-white ring-2 ring-amber-400';
              } else if (gap >= avg) {
                verdict = 'DUE';
                verdictCls = 'bg-rose-500 text-white';
              } else if (gap >= avg * 0.75) {
                verdict = 'approaching';
                verdictCls = 'bg-amber-400 text-amber-900';
              } else {
                verdict = 'fresh';
                verdictCls = 'bg-emerald-500 text-white';
              }
              const gapCls = avg == null ? 'text-gray-500' :
                gap >= 2 * avg ? 'text-rose-700 font-black' :
                gap >= avg ? 'text-rose-500 font-black' :
                gap >= avg * 0.75 ? 'text-amber-700 font-bold' :
                'text-emerald-700';
              const isStreak = r.min_gap != null && r.current_gap === r.min_gap;
              // Extract prediction summary — one number for simple/formula poses, or the
              // per-ball predicted value for triplet children.
              let predChips = null;
              if (r.isParent) {
                predChips = null; // Parent triplet rows aggregate per-ball predictions in child rows
              } else if (r.triplet_predicted_value != null) {
                predChips = [{ field: r.triplet_target_field, value: r.triplet_predicted_value }];
              } else if (r.prediction && Array.isArray(r.prediction.targets)) {
                predChips = r.prediction.targets.map(t => ({ field: t.field, value: t.value }));
              }
              const rowBg = r.isParent
                ? 'bg-amber-100/60 border-l-4 border-l-amber-500 font-bold'
                : r.isChild
                ? (idx % 2 === 0 ? 'bg-white pl-6' : 'bg-amber-50/30 pl-6')
                : idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/40';
              return (
                <tr key={r.key} className={rowBg} data-testid={`w37-poses-summary-row-${r.key}`}>
                  <td className={`px-3 py-2 text-center font-mono ${r.isParent ? 'font-black text-amber-900' : r.isChild ? 'text-gray-500 font-bold' : 'font-black text-gray-500'}`}
                    data-testid={`w37-poses-summary-sno-${r.sno}`}>
                    {r.sno}
                  </td>
                  <td className={`px-3 py-2 ${r.isChild ? 'pl-8 text-gray-700' : 'text-slate-800 font-semibold'}`}>
                    <div className={r.isChild ? 'text-[12px]' : 'text-[13px]'}>{r.name}</div>
                    {r.description && (
                      <div className="text-[10px] text-gray-500 italic mt-0.5 leading-snug max-w-lg">{r.description.length > 105 ? r.description.slice(0, 102) + '…' : r.description}</div>
                    )}
                    {r.familyValues && Array.isArray(r.familyValues.values) && r.familyValues.values.length > 0 && (
                      <div className="text-[10px] text-emerald-700 font-mono mt-0.5 leading-snug max-w-lg" data-testid={`w37-poses-summary-family-${r.key}`}>
                        <span className="font-black uppercase tracking-wider text-emerald-800/80 not-italic mr-1">Valid {r.familyValues.label}s:</span>
                        {r.familyValues.values.join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center font-mono font-bold text-slate-700">
                    {avg == null ? <span className="text-gray-400">—</span> : avg}
                  </td>
                  <td className={`px-3 py-2 text-center font-mono ${gapCls}`}>
                    {gap}
                    {isStreak && <div className="text-[8px] text-orange-600 font-black uppercase mt-0.5">match=min</div>}
                    {r.last_event_date && (
                      <div className="text-[9px] text-gray-500 font-mono font-normal mt-0.5" data-testid={`w37-poses-summary-last-hit-${r.key}`}>
                        last hit: {r.last_event_date}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {r.min_gap == null ? (
                      <span className="text-gray-400 text-xs">—</span>
                    ) : (
                      <div className="leading-tight" title={`Best (shortest) bounce-back ever recorded. The gap of ${r.min_gap} was last observed on ${r.min_gap_last_date || '—'}. This is NOT the last-hit date — see 'last hit' under Current Gap.`}>
                        <div className="font-mono font-black text-purple-800 text-sm">{r.min_gap}</div>
                        <div className="text-[9px] text-gray-500 font-mono">since: {r.min_gap_last_date || '—'}</div>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center font-mono font-bold text-indigo-800">
                    {r.avg_per_year_till_2025 ?? 0}
                    <div className="text-[9px] text-gray-500 font-mono">({r.count_till_2025 ?? 0} total)</div>
                  </td>
                  <td className="px-3 py-2 text-center font-mono font-black text-emerald-700">
                    {r.count_2026 ?? 0}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {predChips ? (
                      <div className="flex flex-wrap justify-center gap-1">
                        {predChips.map((c, ci) => (
                          <div key={ci} className="flex flex-col items-center" data-testid={`w37-pred-${r.key}-${ci}`}>
                            <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-black text-[10px] ${c.value == null || (c.value > 50 && c.field?.startsWith('ball')) ? 'bg-gray-200 text-gray-500' : verdict === 'DUE' || verdict.startsWith('overdue') ? 'bg-rose-600 text-white' : verdict === 'approaching' ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white'}`}>
                              {c.value == null ? '—' : c.value}
                            </span>
                            <span className="text-[8px] text-gray-500 uppercase font-bold mt-0.5">
                              {(c.field || '').replace('ball_', 'B').replace('lucky_star_', 'LS')}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[9px] text-gray-400 italic">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-block px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider ${verdictCls}`}>
                      {verdict}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 bg-amber-50/60 border-t border-amber-200 flex flex-wrap items-center gap-3 text-[10px] text-amber-900">
        <span className="font-black uppercase tracking-wider">Verdict scale:</span>
        <span className="inline-block px-2 py-0.5 rounded bg-emerald-500 text-white font-black">fresh</span>
        <span>gap &lt; 0.75×avg (no rebound signal yet)</span>
        <span className="inline-block px-2 py-0.5 rounded bg-amber-400 text-amber-900 font-black">approaching</span>
        <span>gap ≥ 0.75×avg</span>
        <span className="inline-block px-2 py-0.5 rounded bg-rose-500 text-white font-black">DUE</span>
        <span>gap ≥ avg (fire expected)</span>
        <span className="inline-block px-2 py-0.5 rounded bg-rose-700 text-white ring-2 ring-amber-400 font-black">overdue</span>
        <span>gap ≥ 2×avg (very ripe)</span>
        <span className="italic ml-2 text-amber-800/80">Verdict compares Current Gap vs Avg Bounce-Back — <b>FRESH does not mean “just hit”</b>, it means the pose is still well within its normal recurrence cycle.</span>
      </div>
      </div>
    </div>
  );
}

// ---------- Same Date Repeat table ----------
// Per-ball statistics for the "value at Ball K equals a value at ANY position
// of a prior draw sharing the same day+month" rule. Fed by the dedicated
// /api/analysis/w37-same-date-repeat endpoint so we can iterate on this
// separately from the main yoga-poses payload.
function SameDateRepeatTable({ sameDate }) {
  if (!sameDate) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 italic" data-testid="w37-same-date-empty">
        No data available. Try refreshing the page.
      </div>
    );
  }

  const rows = sameDate.by_ball || [];
  const td = sameDate.target_date || {};
  const dayMon = (td.day != null && td.month != null)
    ? `${String(td.day).padStart(2, '0')}/${String(td.month).padStart(2, '0')}`
    : '—';

  // Pill tone helpers — green ≥ 50%, amber 25-49%, slate < 25%
  const hitPctPill = (p) => {
    if (p >= 50) return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    if (p >= 25) return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-slate-100 text-slate-700 border-slate-300';
  };
  const gapPill = (gap, avg) => {
    if (avg == null) return 'text-slate-500';
    if (gap >= 2 * avg) return 'text-rose-700 font-black';
    if (gap >= avg) return 'text-amber-700 font-bold';
    return 'text-emerald-700 font-bold';
  };

  return (
    <div className="space-y-5" data-testid="w37-same-date-tab">
      {/* Header card — next target date + pool preview */}
      <div className="rounded-2xl border-2 border-indigo-400 bg-white shadow-lg overflow-hidden" data-testid="w37-same-date-header">
        <div className="px-5 py-3 bg-gradient-to-r from-indigo-700 via-indigo-600 to-blue-600 text-white flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-black uppercase tracking-wider">📅 Same Day/Month Repeat</h3>
            <p className="text-[11px] text-white/85 italic">
              For each ball K in a draw, count how often the value also appeared at ANY position in a prior draw sharing the same day+month (any year).
              Next draw target: <b>{td.next_draw_date || '—'}</b> · calendar slot <b>{dayMon}</b>
            </p>
          </div>
          <span className="text-[10px] font-mono bg-white/20 rounded px-2 py-1" data-testid="w37-same-date-pool-size">
            {sameDate.pool_size || 0} numbers in {dayMon} pool
          </span>
        </div>
        {(sameDate.pool || []).length > 0 && (
          <div className="px-4 py-3 border-b border-indigo-100 bg-indigo-50/50">
            <div className="text-[10px] font-black uppercase tracking-wider text-indigo-700 mb-1">
              Historical pool — values ever seen at any position on {dayMon} (any year)
            </div>
            <div className="flex flex-wrap gap-1" data-testid="w37-same-date-pool-chips">
              {sameDate.pool.map((n) => (
                <span key={n} className="inline-flex items-center justify-center min-w-[26px] h-[22px] px-1.5 rounded bg-indigo-100 border border-indigo-300 text-indigo-800 font-mono font-bold text-[11px]">{n}</span>
              ))}
            </div>
          </div>
        )}
        {(sameDate.same_date_history || []).length > 0 && (
          <div className="px-4 py-3 text-[11px]">
            <div className="text-[10px] font-black uppercase tracking-wider text-indigo-700 mb-1.5">
              Prior draws on {dayMon} ({(sameDate.same_date_history || []).length} draw{(sameDate.same_date_history || []).length === 1 ? '' : 's'})
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
              {sameDate.same_date_history.map((h) => (
                <div key={h.draw_number} className="flex items-center gap-2 rounded border border-indigo-100 bg-white px-2 py-1">
                  <span className="text-[10px] font-mono text-indigo-500">#{h.draw_number}</span>
                  <span className="text-[10px] font-bold text-slate-700">{h.draw_date}</span>
                  <span className="flex gap-0.5 ml-auto font-mono text-[10px]">
                    {h.balls.map((b, bi) => (
                      <span key={bi} className="inline-flex items-center justify-center w-[18px] h-[18px] rounded bg-slate-100 border border-slate-300 text-slate-700 font-bold">{b}</span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Per-ball stats table */}
      <div className="rounded-2xl border-2 border-indigo-300 bg-white shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="w37-same-date-table">
            <thead className="bg-indigo-50 border-b-2 border-indigo-200">
              <tr>
                <th className="px-3 py-2 text-center uppercase text-indigo-800 font-black w-14">Ball</th>
                <th className="px-2 py-2 text-center uppercase text-indigo-800 font-black">Hit&nbsp;% since 2024</th>
                <th className="px-2 py-2 text-center uppercase text-indigo-800 font-black">Times total</th>
                <th className="px-2 py-2 text-center uppercase text-indigo-800 font-black">Till 2025</th>
                <th className="px-2 py-2 text-center uppercase text-indigo-800 font-black">2026</th>
                <th className="px-2 py-2 text-center uppercase text-indigo-800 font-black">Avg / year <span className="opacity-60">(till 2025)</span></th>
                <th className="px-2 py-2 text-center uppercase text-indigo-800 font-black">Last hit</th>
                <th className="px-2 py-2 text-center uppercase text-indigo-800 font-black">Avg bounce-back</th>
                <th className="px-2 py-2 text-center uppercase text-indigo-800 font-black">Best bounce</th>
                <th className="px-2 py-2 text-center uppercase text-indigo-800 font-black">Current gap</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.ball} className={`border-b border-indigo-50 ${i % 2 === 0 ? 'bg-white' : 'bg-indigo-50/25'}`} data-testid={`w37-same-date-row-${r.ball}`}>
                  <td className="px-3 py-2.5 text-center">
                    <span className="inline-flex items-center justify-center rounded-md bg-indigo-600 text-white font-black text-sm w-9 h-8">B{r.ball}</span>
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    <span className={`inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-mono font-black text-[12px] ${hitPctPill(r.hit_pct_since_2024)}`}
                          title={`${r.count_since_2024} hits ÷ ${r.draws_since_2024} draws since 2024`}>
                      {r.hit_pct_since_2024?.toFixed(1)}%
                    </span>
                    <div className="text-[9px] text-slate-500 mt-0.5">{r.count_since_2024} / {r.draws_since_2024}</div>
                  </td>
                  <td className="px-2 py-2.5 text-center font-mono font-bold text-slate-800">{r.count_total}</td>
                  <td className="px-2 py-2.5 text-center font-mono text-slate-600">{r.count_till_2025}</td>
                  <td className="px-2 py-2.5 text-center font-mono font-bold text-emerald-700">{r.count_2026}</td>
                  <td className="px-2 py-2.5 text-center font-mono text-slate-600">{r.avg_per_year_till_2025?.toFixed(2)}</td>
                  <td className="px-2 py-2.5 text-center text-slate-700 text-[11px]">
                    {r.last_hit ? (
                      <div>
                        <div className="font-bold">{r.last_hit.draw_date}</div>
                        <div className="text-[9px] text-slate-500 font-mono">#{r.last_hit.draw_number} · value <span className="text-indigo-700 font-black">{r.last_hit.value}</span></div>
                      </div>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-2 py-2.5 text-center font-mono text-slate-700">
                    {r.avg_bounce_back != null ? r.avg_bounce_back.toFixed(2) : '—'}
                    <span className="text-[9px] text-slate-500 ml-0.5">draws</span>
                  </td>
                  <td className="px-2 py-2.5 text-center font-mono text-emerald-700 font-black">
                    {r.min_bounce_back ?? '—'}
                    {r.min_bounce_back != null && <span className="text-[9px] text-slate-500 ml-0.5">draws</span>}
                    {r.min_bounce_back_last_date && (
                      <div className="text-[9px] text-slate-500 font-normal">last {r.min_bounce_back_last_date}</div>
                    )}
                  </td>
                  <td className={`px-2 py-2.5 text-center font-mono ${gapPill(r.current_gap, r.avg_bounce_back)}`}>
                    {r.current_gap}
                    <span className="text-[9px] text-slate-500 ml-0.5">draws</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 bg-slate-50 border-t border-indigo-100 text-[10px] text-slate-600 italic leading-relaxed">
          <b>Hit % since 2024</b> = matches ÷ total draws since 2024, showing the raw firing rate per ball position in the modern era.
          &nbsp;<b>Avg bounce-back</b> = mean draws between consecutive hits (lower = pose fires more frequently).
          &nbsp;<b>Best bounce</b> = smallest gap ever recorded (with the last date it happened).
          &nbsp;<b>Current gap</b> is coloured emerald when below average, amber when past it, rose when past 2× (overdue).
        </div>
      </div>

      {/* ==================================================================
          POSITION-STRICT VARIANT — the pool for each Ball K only contains
          values that ever landed at that exact Ball K slot on the target
          day/month. Stricter than the "any position" table above; useful
          for spotting when a specific slot has a strong same-date memory.
          ================================================================== */}
      {(sameDate.by_ball_same_position || []).length > 0 && (
        <div className="rounded-2xl border-2 border-fuchsia-400 bg-white shadow-lg overflow-hidden" data-testid="w37-same-date-strict">
          <div className="px-5 py-3 bg-gradient-to-r from-fuchsia-700 via-fuchsia-600 to-purple-600 text-white flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider">🎯 Same Day/Month · Position-Strict</h3>
              <p className="text-[11px] text-white/85 italic">
                For each ball K, count how often the value also appeared at the same Ball K position in a prior draw sharing this day+month (any year).
                Stricter than the table above.
              </p>
            </div>
            <span className="text-[10px] font-mono bg-white/20 rounded px-2 py-1">
              5 position-strict pools for {dayMon}
            </span>
          </div>

          {/* Per-Ball K pools — one row per position with its own pool chips */}
          <div className="px-4 py-3 border-b border-fuchsia-100 bg-fuchsia-50/40 space-y-2" data-testid="w37-same-date-strict-pools">
            {[1, 2, 3, 4, 5].map((K) => {
              const poolK = (sameDate.per_position_pool || {})[String(K)] || [];
              const histK = (sameDate.per_position_history || {})[String(K)] || [];
              return (
                <div key={K} className="flex flex-wrap items-center gap-2" data-testid={`w37-same-date-strict-pool-b${K}`}>
                  <span className="inline-flex items-center justify-center rounded bg-fuchsia-600 text-white font-black text-[11px] px-1.5 h-6 min-w-[26px]">B{K}</span>
                  <span className="text-[10px] text-fuchsia-800 font-bold uppercase tracking-wider">Pool</span>
                  <span className="flex flex-wrap gap-1">
                    {poolK.length === 0 && <span className="text-[11px] text-slate-500 italic">no prior values</span>}
                    {poolK.map((n) => (
                      <span key={n} className="inline-flex items-center justify-center min-w-[24px] h-[20px] px-1 rounded bg-fuchsia-100 border border-fuchsia-300 text-fuchsia-800 font-mono font-bold text-[10px]">{n}</span>
                    ))}
                  </span>
                  {histK.length > 0 && (
                    <span className="ml-2 text-[9px] text-slate-500">
                      from {histK.length} prior draw{histK.length === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Per-ball K stats table — mirrors the layout of the primary table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="w37-same-date-strict-table">
              <thead className="bg-fuchsia-50 border-b-2 border-fuchsia-200">
                <tr>
                  <th className="px-3 py-2 text-center uppercase text-fuchsia-800 font-black w-14">Ball</th>
                  <th className="px-2 py-2 text-center uppercase text-fuchsia-800 font-black">Hit&nbsp;% since 2024</th>
                  <th className="px-2 py-2 text-center uppercase text-fuchsia-800 font-black">Times total</th>
                  <th className="px-2 py-2 text-center uppercase text-fuchsia-800 font-black">Till 2025</th>
                  <th className="px-2 py-2 text-center uppercase text-fuchsia-800 font-black">2026</th>
                  <th className="px-2 py-2 text-center uppercase text-fuchsia-800 font-black">Avg / year <span className="opacity-60">(till 2025)</span></th>
                  <th className="px-2 py-2 text-center uppercase text-fuchsia-800 font-black">Last hit</th>
                  <th className="px-2 py-2 text-center uppercase text-fuchsia-800 font-black">Avg bounce-back</th>
                  <th className="px-2 py-2 text-center uppercase text-fuchsia-800 font-black">Best bounce</th>
                  <th className="px-2 py-2 text-center uppercase text-fuchsia-800 font-black">Current gap</th>
                </tr>
              </thead>
              <tbody>
                {(sameDate.by_ball_same_position || []).map((r, i) => (
                  <tr key={r.ball} className={`border-b border-fuchsia-50 ${i % 2 === 0 ? 'bg-white' : 'bg-fuchsia-50/25'}`} data-testid={`w37-same-date-strict-row-${r.ball}`}>
                    <td className="px-3 py-2.5 text-center">
                      <span className="inline-flex items-center justify-center rounded-md bg-fuchsia-600 text-white font-black text-sm w-9 h-8">B{r.ball}</span>
                    </td>
                    <td className="px-2 py-2.5 text-center">
                      <span className={`inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-mono font-black text-[12px] ${hitPctPill(r.hit_pct_since_2024)}`}
                            title={`${r.count_since_2024} hits ÷ ${r.draws_since_2024} draws since 2024`}>
                        {r.hit_pct_since_2024?.toFixed(1)}%
                      </span>
                      <div className="text-[9px] text-slate-500 mt-0.5">{r.count_since_2024} / {r.draws_since_2024}</div>
                    </td>
                    <td className="px-2 py-2.5 text-center font-mono font-bold text-slate-800">{r.count_total}</td>
                    <td className="px-2 py-2.5 text-center font-mono text-slate-600">{r.count_till_2025}</td>
                    <td className="px-2 py-2.5 text-center font-mono font-bold text-emerald-700">{r.count_2026}</td>
                    <td className="px-2 py-2.5 text-center font-mono text-slate-600">{r.avg_per_year_till_2025?.toFixed(2)}</td>
                    <td className="px-2 py-2.5 text-center text-slate-700 text-[11px]">
                      {r.last_hit ? (
                        <div>
                          <div className="font-bold">{r.last_hit.draw_date}</div>
                          <div className="text-[9px] text-slate-500 font-mono">#{r.last_hit.draw_number} · value <span className="text-fuchsia-700 font-black">{r.last_hit.value}</span></div>
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-2.5 text-center font-mono text-slate-700">
                      {r.avg_bounce_back != null ? r.avg_bounce_back.toFixed(2) : '—'}
                      <span className="text-[9px] text-slate-500 ml-0.5">draws</span>
                    </td>
                    <td className="px-2 py-2.5 text-center font-mono text-emerald-700 font-black">
                      {r.min_bounce_back ?? '—'}
                      {r.min_bounce_back != null && <span className="text-[9px] text-slate-500 ml-0.5">draws</span>}
                      {r.min_bounce_back_last_date && (
                        <div className="text-[9px] text-slate-500 font-normal">last {r.min_bounce_back_last_date}</div>
                      )}
                    </td>
                    <td className={`px-2 py-2.5 text-center font-mono ${gapPill(r.current_gap, r.avg_bounce_back)}`}>
                      {r.current_gap}
                      <span className="text-[9px] text-slate-500 ml-0.5">draws</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-slate-50 border-t border-fuchsia-100 text-[10px] text-slate-600 italic leading-relaxed">
            Same metric definitions as the table above — but here <b>a hit only counts if the value appeared at the identical Ball&nbsp;K position</b> in a prior same-date draw. Expected to be lower than the any-position rate; when it still stays high the slot has a strong positional memory for this calendar date.
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- page ----------
export default function Window37Yoga() {
  const navigate = useNavigate();
  const exportRoot = useRef(null);
  const [activeTab, setActiveTab] = useState('summary');
  const { yoga, sameDate: sameDateResource, fetchData, retryYoga, retrySameDate } = useW37Data();
  const { data, loading, error } = yoga;
  const sameDate = sameDateResource.data;

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);

  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const poseA = data?.poses?.pose_ls1_swap; // Pose 1 — LS1 → LS2
  const poseB = data?.poses?.pose_ls2_swap; // Pose 2 — LS2 → LS1
  const poseX = data?.poses?.pose_ls1_x2;   // Pose X — LS1 × 2 → LS2 (number TBD)
  const poseY = data?.poses?.pose_ls2_div2; // Pose Y — LS2 ÷ 2 → LS1 (number TBD)
  const poseALS = data?.poses?.pose_a_ls_ratio; // Pose A — LS2 = LS1 × N (same draw)
  const poseBRep = data?.poses?.pose_b_ls1_repeat; // Pose B — LS1 → LS1 (repeat)
  const poseCRep = data?.poses?.pose_c_ls2_repeat; // Pose C — LS2 → LS2 (repeat)
  const poseD1 = data?.poses?.pose_d1_b1_repeat; // Pose D1 — Ball 1 → Ball 1 (repeat)
  const poseD2 = data?.poses?.pose_d2_b2_repeat; // Pose D2 — Ball 2 → Ball 2 (repeat)
  const poseD3 = data?.poses?.pose_d3_b3_repeat; // Pose D3 — Ball 3 → Ball 3 (repeat)
  const poseD4 = data?.poses?.pose_d4_b4_repeat; // Pose D4 — Ball 4 → Ball 4 (repeat)
  const poseD5 = data?.poses?.pose_d5_b5_repeat; // Pose D5 — Ball 5 → Ball 5 (repeat)
  const pose1 = data?.poses?.pose_1;
  const pose2 = data?.poses?.pose_2;
  const pose3 = data?.poses?.pose_3;
  const pose4 = data?.poses?.pose_4;
  const pose5 = data?.poses?.pose_5;
  const pose6 = data?.poses?.pose_6;
  const pose7 = data?.poses?.pose_7;
  const pose8 = data?.poses?.pose_8;
  const pose9 = data?.poses?.pose_9;
  const pose10 = data?.poses?.pose_10;
  const pose11 = data?.poses?.pose_11;
  const pose12 = data?.poses?.pose_12;
  const eventsA = poseA?.events || [];
  const eventsB = poseB?.events || [];
  const eventsX = poseX?.events || [];
  const eventsY = poseY?.events || [];
  const eventsALS = poseALS?.events || [];
  const eventsBRep = poseBRep?.events || [];
  const eventsCRep = poseCRep?.events || [];
  const eventsD1 = poseD1?.events || [];
  const eventsD2 = poseD2?.events || [];
  const eventsD3 = poseD3?.events || [];
  const eventsD4 = poseD4?.events || [];
  const eventsD5 = poseD5?.events || [];
  const events1 = pose1?.events || [];
  const events2 = pose2?.events || [];
  const events3 = pose3?.events || [];
  const events4 = pose4?.events || [];
  const events5 = pose5?.events || [];
  const events6 = pose6?.events || [];
  const events7 = pose7?.events || [];
  const events8 = pose8?.events || [];
  const events9 = pose9?.events || [];
  const events10 = pose10?.events || [];
  const events11 = pose11?.events || [];
  const events12 = pose12?.events || [];

  const exportCsv = (events, filename, headerNumLabel) => {
    if (!events.length) { toast.error('No events to export'); return; }
    const HEADERS = ['#', 'Prev Draw', 'Prev Date', headerNumLabel, 'Curr Draw', 'Curr Date', 'Year'];
    const rows = [HEADERS, ...events.map((e, i) => [
      i + 1, e.prev_draw_number, e.prev_date,
      (e.numbers && e.numbers.length ? e.numbers.join(' + ') : (e.number ?? '')),
      e.curr_draw_number, e.curr_date, e.year,
    ])];
    const csv = rows.map(r =>
      r.map(c => {
        const s = c == null ? '' : String(c);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')
    ).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `${filename}_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filename} exported`);
  };

  const handleExportPoseA = () => exportCsv(eventsA, 'W37_YogaPose1_LS1toLS2', 'Number (LS₁ → LS₂)');
  const handleExportPoseB = () => exportCsv(eventsB, 'W37_YogaPose2_LS2toLS1', 'Number (LS₂ → LS₁)');
  const handleExportPoseX = () => exportCsv(eventsX, 'W37_YogaPoseX_LS1x2toLS2', 'Number (LS₁ × 2 → LS₂)');
  const handleExportPoseY = () => exportCsv(eventsY, 'W37_YogaPoseY_LS2div2toLS1', 'Number (LS₂ ÷ 2 → LS₁)');
  const handleExportPoseALS = () => exportCsv(eventsALS, 'W37_YogaPoseA_LSRatio', 'Multiplier (LS₂ ÷ LS₁)');
  const handleExportPoseBRep = () => exportCsv(eventsBRep, 'W37_YogaPoseB_LS1Repeat', 'LS₁ value (repeat)');
  const handleExportPoseCRep = () => exportCsv(eventsCRep, 'W37_YogaPoseC_LS2Repeat', 'LS₂ value (repeat)');
  const handleExportPoseD1 = () => exportCsv(eventsD1, 'W37_YogaPoseD1_Ball1Repeat', 'Ball 1 value (repeat)');
  const handleExportPoseD2 = () => exportCsv(eventsD2, 'W37_YogaPoseD2_Ball2Repeat', 'Ball 2 value (repeat)');
  const handleExportPoseD3 = () => exportCsv(eventsD3, 'W37_YogaPoseD3_Ball3Repeat', 'Ball 3 value (repeat)');
  const handleExportPoseD4 = () => exportCsv(eventsD4, 'W37_YogaPoseD4_Ball4Repeat', 'Ball 4 value (repeat)');
  const handleExportPoseD5 = () => exportCsv(eventsD5, 'W37_YogaPoseD5_Ball5Repeat', 'Ball 5 value (repeat)');
  const handleExportPose1 = () => exportCsv(events1, 'W37_YogaPose3', 'Number (LS1 → B1)');
  const handleExportPose2 = () => exportCsv(events2, 'W37_YogaPose4', 'Number (LS2 → B1)');
  const handleExportPose3 = () => exportCsv(events3, 'W37_YogaPose5', 'Numbers (LS1→B1, LS2→B2)');
  const handleExportPose4 = () => exportCsv(events4, 'W37_YogaPose6', 'Number (LS1 → B2)');
  const handleExportPose5 = () => exportCsv(events5, 'W37_YogaPose7', 'Number (LS2 → B2)');
  const handleExportPose6 = () => exportCsv(events6, 'W37_YogaPose8', 'Number (LS1+LS2 → B1)');
  const handleExportPose7 = () => exportCsv(events7, 'W37_YogaPose9', 'Number (LS1+LS2 → B2)');
  const handleExportPose8 = () => exportCsv(events8, 'W37_YogaPose10', 'Number (LS2−LS1 → B1)');
  const exportTriplet = (events, filename, operatorLabel) => {
    if (!events.length) { toast.error('No events to export'); return; }
    const HEADERS = ['#', 'Ball', 'N-2 Draw', 'N-2 Date', 'N-2 Val', 'N-1 Draw', 'N-1 Date', 'N-1 Val', 'Curr Draw', 'Curr Date', 'Match', 'Year'];
    const rows = [HEADERS, ...events.map((e, i) => [
      i + 1, `B${e.ball}`,
      e.draw_n2, e.date_n2, e.value_n2,
      e.draw_n1, e.date_n1, e.value_n1,
      e.draw_curr, e.date_curr, e.value_curr,
      e.year,
    ])];
    const csv = rows.map(r => r.map(c => {
      const s = c == null ? '' : String(c);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `${filename}_${stamp}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filename} exported`);
  };
  const handleExportPose9 = () => exportTriplet(events9, 'W37_YogaPose9_SameBallSum', '+');
  const handleExportPose10 = () => exportTriplet(events10, 'W37_YogaPose10_SameBallDiff', '-');
  const handleExportPose12 = () => {
    if (!events12.length) { toast.error('No events to export'); return; }
    const HEADERS = ['#', 'Ball', 'N-2 Draw', 'N-2 Date', 'N-2 Val', 'N-1 Draw', 'N-1 Date', 'N-1 Val (skipped)', 'Curr Draw', 'Curr Date', 'Curr Val', 'Year'];
    const rows = [HEADERS, ...events12.map((e, i) => [
      i + 1, `B${e.ball}`,
      e.draw_n2, e.date_n2, e.value_n2,
      e.draw_n1, e.date_n1, e.value_n1,
      e.draw_curr, e.date_curr, e.value_curr,
      e.year,
    ])];
    const csv = rows.map(r => r.map(c => {
      const s = c == null ? '' : String(c);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `W37_YogaPose12_SameBallEcho_${stamp}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('W37_YogaPose12_SameBallEcho exported');
  };
  const handleExportPose11 = () => {
    if (!events11.length) { toast.error('No events to export'); return; }
    const HEADERS = ['#', 'Curr Draw', 'Curr Date', 'Depth', 'Numbers', 'Year', 'Detail'];
    const rows = [HEADERS, ...events11.map((e, i) => [
      i + 1, e.curr_draw_number, e.curr_date, e.depth,
      (e.numbers || []).join(' + '),
      e.year,
      (e.matches || []).map(m => `${m.curr_ball.toUpperCase().replace('BALL_','B')}=${m.value} ← ${m.prev_ball.toUpperCase().replace('BALL_','B')} of ${m.prev_date}`).join(' | '),
    ])];
    const csv = rows.map(r =>
      r.map(c => {
        const s = c == null ? '' : String(c);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')
    ).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `W37_YogaPose11_DiagonalPickup_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('W37_YogaPose11_DiagonalPickup exported');
  };

  return (
    <div ref={exportRoot} className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50">
      <Toaster position="top-right" richColors />

      <header className="bg-white/80 backdrop-blur border-b border-amber-200 sticky top-0 z-20" data-testid="w37-page-header">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between min-h-16 gap-2 py-2 sm:py-0">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-slate-500 hover:text-slate-700"
                data-testid="back-btn"
              >
                <ArrowLeft size={18} />
              </Button>
              <div className="w-10 h-10 shrink-0 bg-gradient-to-br from-orange-400 to-rose-500 rounded-full flex items-center justify-center shadow-md">
                <Sun size={22} weight="fill" className="text-white" />
              </div>
              <div className="min-w-0">
                <h1
                  className="text-2xl font-bold tracking-tight text-slate-900"
                  style={{ fontFamily: 'var(--font-heading)' }}
                  data-testid="w37-title"
                >
                  Window 37
                </h1>
                <p className="text-xs text-amber-700 font-semibold">Yoga Poses — structural crossovers between draws</p>
              </div>
            </div>
            <W37VisibleExcelButton rootRef={exportRoot} view={activeTab} data={data} stale={Boolean(error)} />
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
        {/* Purpose / vision banner */}
        <div className="rounded-2xl border border-amber-200 bg-white/70 backdrop-blur px-5 py-4 shadow-sm" data-testid="w37-purpose">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-200 to-rose-200 flex items-center justify-center shrink-0">
              <Sparkle size={20} weight="fill" className="text-amber-700" />
            </div>
            <div>
              <h2 className="text-sm font-extrabold text-slate-900 uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>The Doctor-Patient Method</h2>
              <p className="text-xs text-slate-700 leading-relaxed mt-1 max-w-3xl">
                Every <strong>Yoga Pose</strong> is a structural <em>symptom</em>: a number from one draw &ldquo;reincarnates&rdquo; at a specific position in the next.
                Each pose&apos;s bounce-back rate, lifetime count and current gap form the <em>diagnostic chart</em> — when the current gap blows past the average, the
                pose is &ldquo;due&rdquo; and becomes a <em>medicine</em> we prescribe for the next ticket.
              </p>
            </div>
          </div>
        </div>

        {error && <W37LoadNotice scope="summary" message={error} retained={Boolean(data)} loading={loading} onRetry={retryYoga} />}
        {yoga.invalidPoseCount > 0 && <W37LoadNotice scope="pose-data" message="Some pose details are unavailable. Valid poses remain visible." loading={loading} onRetry={retryYoga} />}
        {loading && !data ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-state">
            <CircleNotch size={32} className="animate-spin text-amber-500" />
          </div>
        ) : data && Object.keys(data.poses).length > 0 ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-testid="w37-tabs">
            <TabsList className="bg-white border-2 border-amber-300 shadow-sm flex flex-wrap h-auto justify-start max-w-full gap-1" data-testid="w37-tab-list">
              <TabsTrigger value="summary" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white font-bold" data-testid="tab-summary">
                📋 Poses Summary — vitals
              </TabsTrigger>
              <TabsTrigger value="detail" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white font-bold" data-testid="tab-detail">
                🧘 Poses Detail
              </TabsTrigger>
              <TabsTrigger value="same-date" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white font-bold" data-testid="tab-same-date">
                📅 Same Date Repeat
              </TabsTrigger>
            </TabsList>

            <TabsContent value="summary" data-w37-export-view="summary" data-testid="w37-summary-panel">
              <PosesSummaryTable
                poses={data?.poses}
                nYearsTill2025={data?.n_years_till_2025}
                recentDraw={data?.recent_draw}
                previousDraw={data?.previous_draw}
                previousPreviousDraw={data?.previous_previous_draw}
                neverInMonthPoses={data?.never_in_month_poses}
                neverInMonthMeta={data?.never_in_month_meta}
              />
            </TabsContent>

            <TabsContent value="same-date">
              {sameDateResource.error && <W37LoadNotice scope="same-date" message={sameDateResource.error} retained={Boolean(sameDate)} loading={sameDateResource.loading} onRetry={retrySameDate} />}
              {sameDateResource.loading && !sameDate ? (
                <div role="status" className="flex items-center justify-center gap-2 py-12 text-sm text-amber-800" data-testid="w37-same-date-loading"><CircleNotch size={24} className="animate-spin" />Loading Same Date Repeat…</div>
              ) : sameDate ? <SameDateRepeatTable sameDate={sameDate} /> : !sameDateResource.error ? (
                <p className="py-12 text-center text-sm text-slate-500" data-testid="w37-same-date-empty">No Same Date Repeat data available.</p>
              ) : null}
            </TabsContent>

            <TabsContent value="detail" className="space-y-8" data-w37-export-view="detail" data-testid="w37-detail-panel">
              <SymptomProgression progression={data?.symptom_progression} />
            {poseA && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...poseA, key: 'pose-a' }} totalDraws={data?.total_draws} accent="fuchsia" icon={Sparkle} flowPairs={[['LS₁', 'LS₂']]} />
                <EventsTable events={eventsA} onExport={handleExportPoseA} title="Pose 1 — LS₁ → LS₂ event log" accent="fuchsia" testidSuffix="pose-a" />
              </div>
            )}
            {poseB && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...poseB, key: 'pose-b' }} totalDraws={data?.total_draws} accent="violet" icon={Sparkle} flowPairs={[['LS₂', 'LS₁']]} />
                <EventsTable events={eventsB} onExport={handleExportPoseB} title="Pose 2 — LS₂ → LS₁ event log" accent="violet" testidSuffix="pose-b" />
              </div>
            )}
            {pose1 && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...pose1, key: 'pose-1' }} totalDraws={data?.total_draws} accent="amber" icon={Sparkle} flowPairs={[['LS₁', 'B₁']]} />
                <EventsTable events={events1} onExport={handleExportPose1} title="Pose 3 — LS₁ → B₁ event log" accent="amber" testidSuffix="pose-1" />
              </div>
            )}
            {pose2 && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...pose2, key: 'pose-2' }} totalDraws={data?.total_draws} accent="rose" icon={Sparkle} flowPairs={[['LS₂', 'B₁']]} />
                <EventsTable events={events2} onExport={handleExportPose2} title="Pose 4 — LS₂ → B₁ event log" accent="rose" testidSuffix="pose-2" />
              </div>
            )}
            {pose3 && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...pose3, key: 'pose-3' }} totalDraws={data?.total_draws} accent="indigo" icon={Sparkle} flowPairs={[['LS₁', 'B₁'], ['LS₂', 'B₂']]} />
                <EventsTable events={events3} onExport={handleExportPose3} title="Pose 5 — LS₁+LS₂ → B₁+B₂ event log" accent="indigo" testidSuffix="pose-3" />
              </div>
            )}
            {pose4 && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...pose4, key: 'pose-4' }} totalDraws={data?.total_draws} accent="emerald" icon={Sparkle} flowPairs={[['LS₁', 'B₂']]} />
                <EventsTable events={events4} onExport={handleExportPose4} title="Pose 6 — LS₁ → B₂ event log" accent="emerald" testidSuffix="pose-4" />
              </div>
            )}
            {pose5 && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...pose5, key: 'pose-5' }} totalDraws={data?.total_draws} accent="teal" icon={Sparkle} flowPairs={[['LS₂', 'B₂']]} />
                <EventsTable events={events5} onExport={handleExportPose5} title="Pose 7 — LS₂ → B₂ event log" accent="teal" testidSuffix="pose-5" />
              </div>
            )}
            {pose6 && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...pose6, key: 'pose-6' }} totalDraws={data?.total_draws} accent="cyan" icon={Sparkle} flowPairs={[['LS₁+LS₂', 'B₁']]} />
                <EventsTable events={events6} onExport={handleExportPose6} title="Pose 8 — LS₁+LS₂ → B₁ event log" accent="cyan" testidSuffix="pose-6" />
              </div>
            )}
            {pose7 && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...pose7, key: 'pose-7' }} totalDraws={data?.total_draws} accent="sky" icon={Sparkle} flowPairs={[['LS₁+LS₂', 'B₂']]} />
                <EventsTable events={events7} onExport={handleExportPose7} title="Pose 9 — LS₁+LS₂ → B₂ event log" accent="sky" testidSuffix="pose-7" />
              </div>
            )}
            {pose8 && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...pose8, key: 'pose-8' }} totalDraws={data?.total_draws} accent="orange" icon={Sparkle} flowPairs={[['LS₂−LS₁', 'B₁']]} />
                <EventsTable events={events8} onExport={handleExportPose8} title="Pose 10 — LS₂−LS₁ → B₁ event log" accent="orange" testidSuffix="pose-8" />
              </div>
            )}
            {pose9 && (
              <div className="space-y-4">
                <TripletCard pose={pose9} totalDraws={data?.total_draws} accent="lime" operator="+" testidSuffix="pose-9" />
                <TripletEventsList events={events9} onExport={handleExportPose9} title="Pose 11 — Same-Ball Sum event log" accent="lime" testidSuffix="pose-9" operatorLabel="+" />
              </div>
            )}
            {pose10 && (
              <div className="space-y-4">
                <TripletCard pose={pose10} totalDraws={data?.total_draws} accent="yellow" operator="−" testidSuffix="pose-10" />
                <TripletEventsList events={events10} onExport={handleExportPose10} title="Pose 12 — Same-Ball Diff event log" accent="yellow" testidSuffix="pose-10" operatorLabel="−" />
              </div>
            )}
            {pose11 && (
              <div className="space-y-4">
                <DiagonalCard pose={pose11} totalDraws={data?.total_draws} />
                <DiagonalEventsList events={events11} onExport={handleExportPose11} />
              </div>
            )}
            {pose12 && (
              <div className="space-y-4">
                <TripletCard pose={pose12} totalDraws={data?.total_draws} accent="sky" operator="=" testidSuffix="pose-12" formulaMode="echo" />
                <TripletEventsList events={events12} onExport={handleExportPose12} title="Pose 14 — Same-Ball Echo event log" accent="sky" testidSuffix="pose-12" operatorLabel="=" />
              </div>
            )}
            {poseX && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...poseX, key: 'pose-x' }} totalDraws={data?.total_draws} accent="pink" icon={Sparkle} flowPairs={[['LS₁ × 2', 'LS₂']]} />
                <EventsTable events={eventsX} onExport={handleExportPoseX} title="Pose X — LS₁ × 2 → LS₂ event log" accent="pink" testidSuffix="pose-x" />
              </div>
            )}
            {poseY && (
              <div className="space-y-4">
                <Pose1Card pose={{ ...poseY, key: 'pose-y' }} totalDraws={data?.total_draws} accent="purple" icon={Sparkle} flowPairs={[['LS₂ ÷ 2', 'LS₁']]} />
                <EventsTable events={eventsY} onExport={handleExportPoseY} title="Pose Y — LS₂ ÷ 2 → LS₁ event log" accent="purple" testidSuffix="pose-y" />
              </div>
            )}
            {poseALS && (
              <div className="space-y-4" data-testid="pose-a-ls-block">
                {/* Pose A — LS2 = LS1 × N (same draw). Intra-draw LS
                    multiplier signal — useful for LS ticket selection when
                    the historical pattern says "these two Lucky Stars will
                    share a clean multiplier relationship" (e.g. LS1=5, LS2=10).
                    Same draw so `prev_` / `curr_` refer to the same row and
                    the discovered multiplier is stored in `numbers[0]`. */}
                <Pose1Card
                  pose={{ ...poseALS, key: 'pose-a-ls' }}
                  totalDraws={data?.total_draws}
                  accent="amber"
                  icon={Sparkle}
                  flowPairs={[['LS₁', 'LS₂ = LS₁ × N']]}
                />
                <EventsTable
                  events={eventsALS}
                  onExport={handleExportPoseALS}
                  title="Pose A — LS₂ = LS₁ × N (same draw) event log"
                  accent="amber"
                  testidSuffix="pose-a-ls"
                />
              </div>
            )}
            {poseBRep && (
              <div className="space-y-4" data-testid="pose-b-ls1-repeat-block">
                {/* Pose B — LS1 repeats. Same-position Lucky Star 1 repeat:
                    LS1 of the previous draw comes back as LS1 in the very next
                    draw. Max Drought vs Current Drought comparison (surfaced
                    by Pose1Card's Current Gap StatCard + Bounce-back range
                    min/max/avg row) tells you whether an LS1 repeat is due. */}
                <Pose1Card
                  pose={{ ...poseBRep, key: 'pose-b-ls1-repeat' }}
                  totalDraws={data?.total_draws}
                  accent="rose"
                  icon={Sparkle}
                  flowPairs={[['LS₁ (prev)', 'LS₁ (next)']]}
                />
                <EventsTable
                  events={eventsBRep}
                  onExport={handleExportPoseBRep}
                  title="Pose B — LS₁ → LS₁ (repeat) event log"
                  accent="rose"
                  testidSuffix="pose-b-ls1-repeat"
                />
              </div>
            )}
            {poseCRep && (
              <div className="space-y-4" data-testid="pose-c-ls2-repeat-block">
                {/* Pose C — LS2 repeats. Same-position Lucky Star 2 repeat:
                    LS2 of the previous draw comes back as LS2 in the very next
                    draw. Same Max Drought vs Current Drought signal as Pose B
                    but for LS2. */}
                <Pose1Card
                  pose={{ ...poseCRep, key: 'pose-c-ls2-repeat' }}
                  totalDraws={data?.total_draws}
                  accent="indigo"
                  icon={Sparkle}
                  flowPairs={[['LS₂ (prev)', 'LS₂ (next)']]}
                />
                <EventsTable
                  events={eventsCRep}
                  onExport={handleExportPoseCRep}
                  title="Pose C — LS₂ → LS₂ (repeat) event log"
                  accent="indigo"
                  testidSuffix="pose-c-ls2-repeat"
                />
              </div>
            )}
            {/* Pose D family — Main-ball K repeats at position K in the next
                draw. Five poses (D1..D5), one per position. Same Max Drought
                vs Current Drought signal family as Pose B / C but for main
                balls. Each block re-uses Pose1Card + EventsTable so the
                Current Gap StatCard and min/max/avg row prominently expose
                the drought comparison the user asked for. */}
            {poseD1 && (
              <div className="space-y-4" data-testid="pose-d1-b1-repeat-block">
                <Pose1Card
                  pose={{ ...poseD1, key: 'pose-d1-b1-repeat' }}
                  totalDraws={data?.total_draws}
                  accent="emerald"
                  icon={Sparkle}
                  flowPairs={[['B₁ (prev)', 'B₁ (next)']]}
                />
                <EventsTable
                  events={eventsD1}
                  onExport={handleExportPoseD1}
                  title="Pose D1 — Ball 1 → Ball 1 (repeat) event log"
                  accent="emerald"
                  testidSuffix="pose-d1-b1-repeat"
                />
              </div>
            )}
            {poseD2 && (
              <div className="space-y-4" data-testid="pose-d2-b2-repeat-block">
                <Pose1Card
                  pose={{ ...poseD2, key: 'pose-d2-b2-repeat' }}
                  totalDraws={data?.total_draws}
                  accent="teal"
                  icon={Sparkle}
                  flowPairs={[['B₂ (prev)', 'B₂ (next)']]}
                />
                <EventsTable
                  events={eventsD2}
                  onExport={handleExportPoseD2}
                  title="Pose D2 — Ball 2 → Ball 2 (repeat) event log"
                  accent="teal"
                  testidSuffix="pose-d2-b2-repeat"
                />
              </div>
            )}
            {poseD3 && (
              <div className="space-y-4" data-testid="pose-d3-b3-repeat-block">
                <Pose1Card
                  pose={{ ...poseD3, key: 'pose-d3-b3-repeat' }}
                  totalDraws={data?.total_draws}
                  accent="cyan"
                  icon={Sparkle}
                  flowPairs={[['B₃ (prev)', 'B₃ (next)']]}
                />
                <EventsTable
                  events={eventsD3}
                  onExport={handleExportPoseD3}
                  title="Pose D3 — Ball 3 → Ball 3 (repeat) event log"
                  accent="cyan"
                  testidSuffix="pose-d3-b3-repeat"
                />
              </div>
            )}
            {poseD4 && (
              <div className="space-y-4" data-testid="pose-d4-b4-repeat-block">
                <Pose1Card
                  pose={{ ...poseD4, key: 'pose-d4-b4-repeat' }}
                  totalDraws={data?.total_draws}
                  accent="sky"
                  icon={Sparkle}
                  flowPairs={[['B₄ (prev)', 'B₄ (next)']]}
                />
                <EventsTable
                  events={eventsD4}
                  onExport={handleExportPoseD4}
                  title="Pose D4 — Ball 4 → Ball 4 (repeat) event log"
                  accent="sky"
                  testidSuffix="pose-d4-b4-repeat"
                />
              </div>
            )}
            {poseD5 && (
              <div className="space-y-4" data-testid="pose-d5-b5-repeat-block">
                <Pose1Card
                  pose={{ ...poseD5, key: 'pose-d5-b5-repeat' }}
                  totalDraws={data?.total_draws}
                  accent="orange"
                  icon={Sparkle}
                  flowPairs={[['B₅ (prev)', 'B₅ (next)']]}
                />
                <EventsTable
                  events={eventsD5}
                  onExport={handleExportPoseD5}
                  title="Pose D5 — Ball 5 → Ball 5 (repeat) event log"
                  accent="orange"
                  testidSuffix="pose-d5-b5-repeat"
                />
              </div>
            )}
            </TabsContent>
          </Tabs>
        ) : !error ? (
          <div className="text-center py-20 text-slate-500" data-testid="empty-state">
            No pose data available for the recorded draws.
          </div>
        ) : null}
      </main>
    </div>
  );
}
